import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  startTransition,
} from "react";
import { Rnd } from "react-rnd";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Box,
  TextField,
  Collapse,
  Menu,
  MenuItem,
  Paper,
  Divider,
  Tabs,
  Tab,
  Button,
  Checkbox,
  Select,
  // ListItemIcon,
  CircularProgress,
  SvgIcon,
} from "@mui/material";
import {
  Close as CloseIcon,
  Layers as LayersIcon,
  Map as MapIcon,
  Search as SearchIcon,
  Build as BuildIcon,
  Home as HomeIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  MyLocation as MyLocationIcon,
  MoreVert as MoreVertIcon,
  ExpandLess,
  ExpandMore,
  CloudUpload as UploadIcon,
  FirstPage as FirstPageIcon,
  LastPage as LastPageIcon,
  Public as PublicIcon,
  LocationOn as LocationOnIcon,
  CheckCircle as CheckCircleIcon,
  VerticalAlignBottom as DockIcon,
  OpenInNew as UndockIcon,
  // TravelExplore as TravelExploreIcon, //zoom to layer
  TableView as TableViewIcon, //open arrtibute table
  TableChartOutlined as TableChartOutlinedIcon,
} from "@mui/icons-material";

import AppHeader from "./components/TopBar";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import XYZ from "ol/source/XYZ";
import VirtualTable from "./components/VirtualTable";
import {
  ToolDrawerPanels,
  ToolsFloatingBar,
  closeToolState,
  TOOL_ORDER,
  TOOL_LABELS,
} from "./components/tools";
import { GEOSERVER_CONFIG, DEFAULT_WFS_PARAMS } from "./config/api";
import { createLayerGroups } from "./config/layerConfig";
import { useAuth } from "./context/AuthContext";
import {
  exportMapImage,
  buildPrintHtml,
  printInIframe,
} from "./components/tools/print/printLayout";
import {
  listBookmarks,
  createBookmark,
  deleteBookmark,
} from "./services/bookmarkApi";
import {
  buildPopupLayerKeys,
  toggleLayerInGroups,
  toggleGroupInGroups,
  toggleNestedExpandedInGroups,
  syncTileLayerForKey,
  syncGroupTileLayers,
} from "./utils/layerGroups";

import CircleStyle from "ol/style/Circle";
import "ol/ol.css";
import Map from "ol/Map";
import View from "ol/View";
import TileLayer from "ol/layer/Tile";
import TileWMS from "ol/source/TileWMS";
import OSM from "ol/source/OSM";
import { fromLonLat, toLonLat } from "ol/proj";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import Feature from "ol/Feature";
import Point from "ol/geom/Point";
import Overlay from "ol/Overlay";
import { Style, Icon, Stroke, Fill, Text as TextStyle } from "ol/style";
import GeoJSON from "ol/format/GeoJSON";
// Measurement  states
import Draw from "ol/interaction/Draw";
import { getLength, getArea } from "ol/sphere";
import { Polygon, LineString } from "ol/geom";
// import hcgLogo from "./assets/HCG-logo-1.png";

/**
 * GetFeatureInfo click tolerance, pixels me.
 *
 * GeoServer ka default info buffer ~5px hai, jo patli pipelines par click
 * karne ke liye kam padta hai. Live server par measure kiya gaya (100m half-
 * extent, 0.78 m/px):
 *   BUFFER default → ~5px  (3.9m) tak hit
 *   BUFFER 10      → ~10px (7.8m) tak hit
 *   BUFFER 20      → ~20px (15.6m) tak hit
 * 10 comfortable hai — patli line pakadna aasan, aur itna bada bhi nahi ki
 * door ke features ghus aayein. Tolerance badalni ho to sirf ye number badlo.
 */
const FEATURE_INFO_BUFFER_PX = 10;

/** Bookmark tool: items per page (backend allows 1..50). */
const BOOKMARK_PAGE_SIZE = 10;

/** Direction tool: default do khaali stops (start + destination). */
const makeDirectionStops = () => [
  { id: 1, query: "", label: "", lon: null, lat: null },
  { id: 2, query: "", label: "", lon: null, lat: null },
];

const isValidExtent = (extent) => {
  if (!extent || extent.length !== 4) return false;
  if (!extent.every((val) => isFinite(val))) return false;

  const [minX, minY, maxX, maxY] = extent;
  const width = maxX - minX;
  const height = maxY - minY;

  // ✅ Allow very small extents (for points)
  return width >= 0 && height >= 0;
};

const tileLoadWithTimeout = (tile, src) => {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 12000);
  fetch(src, { signal: controller.signal })
    .then((r) => r.blob())
    .then((blob) => {
      clearTimeout(timeout);
      const objectUrl = URL.createObjectURL(blob);
      const image = tile.getImage();
      image.onload = () => URL.revokeObjectURL(objectUrl);
      image.src = objectUrl;
    })
    .catch(() => {
      clearTimeout(timeout);
      tile.setState(3);
    });
};

function App({ onBackToHome, onNavigateToDashboard }) {
  const { user: authUser } = useAuth();
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const coordinatesDivRef = useRef(null);
  const [coordFormat, setCoordFormat] = useState("xy");
  const [scale, setScale] = useState("11541");
  const [scaleInput, setScaleInput] = useState("11541");
  const [activeTab, setActiveTab] = useState(0);
  const [searchOpen, setSearchOpen] = useState(false);
  const [layerFilter, setLayerFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [baseMapMenuAnchor, setBaseMapMenuAnchor] = useState(null);
  const [currentBaseMap, setCurrentBaseMap] = useState("osm");
  // Sidebar (drawer) width. 280 par tab bar turant overflow ho jaata tha
  // (Layers + Legend + ek tool tab = ~292px > ~240px available) aur tool
  // panels ke input fields bahut chhote reh jaate the. 400 par 4-5 tabs bina
  // scroll ke fit hote hain aur panels ko ~368px content width milti hai.
  const DRAWER_DEFAULT_WIDTH = 400;
  const DRAWER_MIN_WIDTH = 280;
  const DRAWER_MAX_WIDTH = 600;
  const [drawerWidth, setDrawerWidth] = useState(DRAWER_DEFAULT_WIDTH);
  const [isResizing, setIsResizing] = useState(false);
  const [layerMenuAnchor, setLayerMenuAnchor] = useState(null);
  const [selectedLayer, setSelectedLayer] = useState(null);
  // Bookmarks: server par user-wise stored, paged listing
  const [bookmarks, setBookmarks] = useState([]); // current page ke items
  const [bookmarkName, setBookmarkName] = useState("");
  const [bookmarkPage, setBookmarkPage] = useState(1);
  const [bookmarkPages, setBookmarkPages] = useState(0);
  const [bookmarkTotal, setBookmarkTotal] = useState(0);
  const [bookmarkLoading, setBookmarkLoading] = useState(false);
  const [bookmarkError, setBookmarkError] = useState("");
  const [attributeTableOpen, setAttributeTableOpen] = useState(false);
  const [attributeData, setAttributeData] = useState([]);
  const [attributeColumns, setAttributeColumns] = useState([]);
  const [attributeLayerName, setAttributeLayerName] = useState("");
  const [attributeLoading, setAttributeLoading] = useState(false);

  const [selectedQueryLayer, setSelectedQueryLayer] = useState(null);
  const [activeFilters, setActiveFilters] = useState({});

  const [tablePage, setTablePage] = useState(0);

  // Print tool (new module: components/tools/print)
  const [printTitle, setPrintTitle] = useState("");
  const [printDpi, setPrintDpi] = useState(200);
  const [printGenerating, setPrintGenerating] = useState(false);
  const [printError, setPrintError] = useState("");
  const [printProgress, setPrintProgress] = useState(null); // {loaded,total}
  const [printLogs, setPrintLogs] = useState([]); // [{t, m}]

  const [activeTools, setActiveTools] = useState([]);
  const [drawType, setDrawType] = useState("Point");
  const [drawnFeatures, setDrawnFeatures] = useState([]);

  const [measureType, setMeasureType] = useState("LineString"); // LineString or Polygon
  const [measurements, setMeasurements] = useState([]);

  const [measureUnit, setMeasureUnit] = useState("meters"); // For length
  const [measureAreaUnit, setMeasureAreaUnit] = useState("sqmeters"); // For area
  const [isMeasuring, setIsMeasuring] = useState(false);
  // Filter tool states
  const [filterLayer, setFilterLayer] = useState("");

  // Select tool state
  const [selectToolActive] = useState(false);

  // Query tool states
  const [queryLayer, setQueryLayer] = useState("");

  // Asset search states

  const [queryClauses, setQueryClauses] = useState([
    { id: Date.now(), field: "", operator: "is equal to", value: "" },
  ]);
  const [invertWhere, setInvertWhere] = useState(false);
  const [queryLayerFields, setQueryLayerFields] = useState([]);
  // Filter ke liye ALAG states
  const [filterClauses, setFilterClauses] = useState([
    { id: Date.now(), field: "", operator: "is equal to", value: "" },
  ]);
  const [filterInvertWhere, setFilterInvertWhere] = useState(false);
  const [filterLayerFields, setFilterLayerFields] = useState([]);

  const [queryFieldValues, setQueryFieldValues] = useState({}); // { clauseId: [values] }
  const [queryFieldValuesLoading, setQueryFieldValuesLoading] = useState({});

  const [selectedRows, setSelectedRows] = useState([]); // selected row ids
  const [tableViewMode, setTableViewMode] = useState("all");
  const [isDrawingActive, setIsDrawingActive] = useState(false);
  const [selectMode, setSelectMode] = useState("rectangle"); // "rectangle" | "freehand"
  const [selectInteractionRef] = useState({ current: null });
  const selectDrawLayerRef = useRef(null); // "all" or "selected"

  const measureOverlayRef = useRef(null);
  const attributeFeaturesRef = useRef([]); // store raw WFS features with geometry
  // Har layer ka asli geometry column name cache karo (kahin "geometry",
  // kahin "the_geom" ho sakta hai) — spatial CQL filter ke liye zaroori
  const geomFieldCacheRef = useRef({});
  // NEW STATE for docked table height
  const [dockedTableHeight, setDockedTableHeight] = useState(300); // Default 300px
  const [isResizingDocked, setIsResizingDocked] = useState(false);

  const [attributeTableDocked, setAttributeTableDocked] = useState(false);
  const [attributeTableSize, setAttributeTableSize] = useState({
    width: 800,
    height: 500,
  });
  const [attributeTablePosition, setAttributeTablePosition] = useState({
    x: 100,
    y: 100,
  });

  const [popupOpen, setPopupOpen] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ x: 100, y: 100 });
  const overlayRef = useRef(null);
  const [allPopupFeatures, setAllPopupFeatures] = useState([]);
  const [expandedAttributes, setExpandedAttributes] = useState({});
  const selectionLayerRef = useRef(null);

  const selectedRowsRef = useRef([]);
  const isDrawingActiveRef = useRef(false); // ADD THIS
  const isPopupClosingRef = useRef(false);
  const isZoomingToFeatureRef = useRef(false);

  // asset secarc near me
  const [assetSearchMode, setAssetSearchMode] = useState("idle"); // 'idle' | 'selecting'
  const [incidentPoint, setIncidentPoint] = useState(null);
  const [searchRange, setSearchRange] = useState(200);
  const [searchUnit, setSearchUnit] = useState("meters");
  const [assetLayerFilter, setAssetLayerFilter] = useState("All");
  const [assetResults, setAssetResults] = useState({});
  const [assetResultsExpanded, setAssetResultsExpanded] = useState({});
  const [assetSearchLoading, setAssetSearchLoading] = useState(false);

  const assetBufferLayerRef = useRef(null);
  const assetPointLayerRef = useRef(null);
  const isAssetSelectingRef = useRef(false);

  // Direction tool (OSRM routing + OSM geocoding)
  // State yahan rakha hai (panel mein nahi) taaki tab switch par route na ude.
  const [directionStops, setDirectionStops] = useState(makeDirectionStops);
  const [directionRoute, setDirectionRoute] = useState(null);
  const [directionLoading, setDirectionLoading] = useState(false);
  const [directionError, setDirectionError] = useState("");
  const [directionPickingId, setDirectionPickingId] = useState(null);
  const [directionActiveStep, setDirectionActiveStep] = useState(null); // {leg, step}
  const directionLayerRef = useRef(null);
  // Jab user "pick on map" dabata hai, panel yahan ek callback rakh deta hai;
  // map ka singleclick handler use ek baar call karke null kar deta hai.
  const directionPickRef = useRef(null);

  // asset secarc near me ENd

  //  WMS layers
  const [layerGroups, setLayerGroups] = useState(createLayerGroups);
  const layerGroupsRef = useRef(layerGroups);
  const popupLayerKeysRef = useRef(new Set());
  const POPUP_EXCLUDED_LAYERS = useRef(["ga", "ca"]);
  // Basemap config
  const baseMaps = {
    osm: {
      name: "OpenStreetMap",
      layer: () =>
        new TileLayer({
          source: new OSM({ crossOrigin: "anonymous" }),
          tileLoadFunction: tileLoadWithTimeout,
        }),
      thumbnail: "https://tile.openstreetmap.org/5/16/10.png",
    },
    worldNavigation: {
      name: "World Navigation Map",
      layer: () =>
        new TileLayer({
          source: new XYZ({
            crossOrigin: "anonymous",
            url: "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}",
            maxZoom: 16,
            tileLoadFunction: tileLoadWithTimeout,
          }),
        }),
      thumbnail:
        "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/5/16/10", // ADD
    },
    worldStreetNight: {
      name: "World Street Map (Night)",
      layer: () =>
        new TileLayer({
          source: new XYZ({
            crossOrigin: "anonymous",
            url: "https://{a-c}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png",
            maxZoom: 19,
            tileLoadFunction: tileLoadWithTimeout,
          }),
        }),
      thumbnail: "https://a.basemaps.cartocdn.com/dark_all/5/16/10.png", // ADD
    },
    imageryLabels: {
      name: "Imagery with Labels",
      layer: () =>
        new TileLayer({
          source: new XYZ({
            crossOrigin: "anonymous",
            url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
            maxZoom: 19,
            tileLoadFunction: tileLoadWithTimeout,
          }),
        }),
      thumbnail:
        "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/5/16/10", // ADD
    },
    topographic: {
      name: "Topographic",
      layer: () =>
        new TileLayer({
          source: new XYZ({
            crossOrigin: "anonymous",
            url: "https://{a-c}.tile.opentopomap.org/{z}/{x}/{y}.png",
            maxZoom: 17,
            tileLoadFunction: tileLoadWithTimeout,
          }),
        }),
      thumbnail: "https://a.tile.opentopomap.org/5/16/10.png", // ADD
    },
  };

  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerLayerRef = useRef(null);
  const baseLayerRef = useRef(null);
  const wmsLayersRef = useRef({});
  const layerExtentsRef = useRef({});
  const layerNamesRef = useRef({});
  const drawInteractionRef = useRef(null);
  const measureLayerRef = useRef(null);
  const drawLayerRef = useRef(null);

  useEffect(() => {
    if (!mapRef.current) return;

    if (mapInstanceRef.current) {
      mapInstanceRef.current.setTarget(null);
      mapInstanceRef.current = null;
    }

    const osmLayer = new TileLayer({
      source: new OSM({
        crossOrigin: "anonymous",
      }),
      name: "osm",
    });
    baseLayerRef.current = osmLayer;

    const vectorSource = new VectorSource();
    const markerLayer = new VectorLayer({
      source: vectorSource,
      name: "markers",
    });
    markerLayerRef.current = markerLayer;

    // Selection layer for highlighting clicked features
    const selectionSource = new VectorSource();
    const selectionLayer = new VectorLayer({
      source: selectionSource,
      name: "selection",
      style: (feature) => {
        const geomType = feature.getGeometry()?.getType();
        if (geomType === "Point" || geomType === "MultiPoint") {
          return new Style({
            image: new CircleStyle({
              radius: 10,
              fill: new Fill({ color: "rgba(0, 191, 255, 0.3)" }),
              stroke: new Stroke({ color: "#00BFFF", width: 4 }),
            }),
          });
        }
        // Lines + Polygons - cyan bold outline
        return new Style({
          stroke: new Stroke({ color: "#00BFFF", width: 4 }),
          fill: new Fill({ color: "rgba(0, 191, 255, 0.15)" }),
        });
      },
      zIndex: 999,
    });
    selectionLayerRef.current = selectionLayer;

    const wmsLayers = createWMSLayers();

    const map = new Map({
      target: mapRef.current,
      layers: [osmLayer, ...wmsLayers, selectionLayer, markerLayer],
      controls: [],
      view: new View({
        center: fromLonLat([76.993869, 28.448841]),
        zoom: 10,
        // Scale = resolution * 39.37 * (25.4/0.28) = resolution * 3571.4
        //   zoom 20 → 0.1493 m/px → 1:533   (purana max)
        //   zoom 25 → 0.0047 m/px → 1:17
        //   zoom 26 → 0.0023 m/px → 1:8.3   (naya max)
        // 1:10 exactly zoom 25.74 par aata hai. constrainResolution:true ki
        // wajah se wheel-zoom integer zoom levels par snap karta hai, isliye
        // 1:10 tak pahunchne ke liye max 26 chahiye. Scale box mein "10" type
        // karne par exactly 1:10 milega (setResolution direct set karta hai).
        maxZoom: 26,
        minZoom: 2,
        constrainResolution: true,
      }),
      pixelRatio: window.devicePixelRatio || 1,
    });

    mapInstanceRef.current = map;
    map
      .getInteractions()
      .getArray()
      .forEach((interaction) => {
        if (interaction.constructor.name === "DragPan") {
          interaction.condition_ = (e) =>
            e.originalEvent.button === 1 || !e.originalEvent.button;
        }
      });

    // draw tab
    // Draw Layer setup
    const drawSource = new VectorSource();
    const drawVector = new VectorLayer({
      source: drawSource,
      style: new Style({
        fill: new Fill({ color: "rgba(255, 0, 0, 0.2)" }),
        stroke: new Stroke({ color: "#ff0000", width: 2 }),
        image: new CircleStyle({
          radius: 6,
          fill: new Fill({ color: "#ff0000" }),
          stroke: new Stroke({ color: "white", width: 2 }),
        }),
      }),
      zIndex: 998,
    });
    drawLayerRef.current = drawVector;
    map.addLayer(drawVector);
    // draw tab end

    // Asset search

    // Asset Buffer Layer
    const bufferSource = new VectorSource();
    const bufferLayer = new VectorLayer({
      source: bufferSource,
      style: new Style({
        fill: new Fill({ color: "rgba(79, 129, 179, 0.1)" }),
        stroke: new Stroke({ color: "#1976d2 ", width: 2, lineDash: [6, 4] }),
      }),
      zIndex: 997,
    });
    assetBufferLayerRef.current = bufferLayer;
    map.addLayer(bufferLayer);

    // Asset Point Layer
    const assetPointSource = new VectorSource();
    const assetPointLayer = new VectorLayer({
      source: assetPointSource,
      style: new Style({
        image: new CircleStyle({
          radius: 8,
          fill: new Fill({ color: "#1976d2" }),
          stroke: new Stroke({ color: "white", width: 2 }),
        }),
      }),
      zIndex: 999,
    });
    assetPointLayerRef.current = assetPointLayer;
    map.addLayer(assetPointLayer);
    // asset search end

    // Direction tool layer: route line + numbered stop markers + active step
    const directionLayer = new VectorLayer({
      source: new VectorSource(),
      zIndex: 1000,
      style: (feature) => {
        const kind = feature.get("kind");
        if (kind === "route") {
          return [
            new Style({ stroke: new Stroke({ color: "white", width: 8 }) }),
            new Style({ stroke: new Stroke({ color: "#1976d2", width: 5 }) }),
          ];
        }
        if (kind === "step") {
          return [
            new Style({ stroke: new Stroke({ color: "white", width: 10 }) }),
            new Style({ stroke: new Stroke({ color: "#ff9800", width: 6 }) }),
          ];
        }
        // stop marker: start green, end red, via blue (panel badges se match)
        const idx = feature.get("index");
        const total = feature.get("total");
        const color =
          idx === 1 ? "#4caf50" : idx === total ? "#f44336" : "#1976d2";
        return new Style({
          image: new CircleStyle({
            radius: 11,
            fill: new Fill({ color }),
            stroke: new Stroke({ color: "white", width: 2.5 }),
          }),
          text: new TextStyle({
            text: String(idx),
            font: "bold 12px sans-serif",
            fill: new Fill({ color: "white" }),
          }),
        });
      },
    });
    directionLayerRef.current = directionLayer;
    map.addLayer(directionLayer);

    // Popup overlay create karo
    const popupElement = document.getElementById("popup-container");
    if (popupElement) {
      const overlay = new Overlay({
        element: popupElement,
        positioning: "bottom-center",
        offset: [0, -20],
        stopEvent: false,
        autoPan: {
          animation: { duration: 250 },
          margin: 30, // ← map ke andar 30px gap rakhega
        },
      });
      overlayRef.current = overlay;
      map.addOverlay(overlay);
    }

    const updateSizeTimer = setTimeout(() => {
      map.updateSize();
    }, 100);

    const handleMoveEnd = () => {
      const view = map.getView();
      const resolution = view.getResolution();
      const units = view.getProjection().getUnits();
      const dpi = 25.4 / 0.28;
      const mpu = units === "degrees" ? 111194.87428468118 : 1;
      const calculatedScale = Math.round(resolution * mpu * 39.37 * dpi);
      setScale(calculatedScale.toString());
      setScaleInput(calculatedScale.toString());
    };

    map.on("moveend", handleMoveEnd);

    // Map click handler - Multiple features ke liye
    const handleSingleClick = async (evt) => {
      if (isDrawingActiveRef.current) return;
      if (isZoomingToFeatureRef.current) {
        isZoomingToFeatureRef.current = false;
        return;
      }
      if (isPopupClosingRef.current) {
        isPopupClosingRef.current = false;
        return;
      }

      // direction tool: "pick on map" active hai to click stop ban jaata hai,
      // feature info nahi khulta
      if (typeof directionPickRef.current === "function") {
        const cb = directionPickRef.current;
        directionPickRef.current = null;
        const [lon, lat] = toLonLat(evt.coordinate);
        cb(lon, lat);
        return;
      }

      // asset search
      if (isAssetSelectingRef.current) {
        const lonLat = toLonLat(evt.coordinate);
        const point = {
          lon: lonLat[0],
          lat: lonLat[1],
          coordinate: evt.coordinate,
        };
        setIncidentPoint(point);
        if (assetPointLayerRef.current) {
          assetPointLayerRef.current.getSource().clear();
          const marker = new Feature({ geometry: new Point(evt.coordinate) });
          assetPointLayerRef.current.getSource().addFeature(marker);
        }
        isAssetSelectingRef.current = false;
        setAssetSearchMode("idle");
        return;
      }
      // asset search end

      const coordinate = evt.coordinate;

      // Clear previous selection only if no attribute table rows are selected
      // Always clear previous selection on new click
      if (selectionLayerRef.current) {
        selectionLayerRef.current.getSource().clear();
        selectionLayerRef.current.changed();
      }

      // Sirf CHECKED + VISIBLE layers hi identify ke liye lo.
      // Teen gates lagti hain:
      //  1. layer.getVisible()  → OL TileLayer visible hai (kai keys ek hi
      //     TileLayer share karte hain, isliye ye group-level check hai)
      //  2. popupLayerKeysRef   → is individual layer ka CHECKBOX on hai
      //     (buildPopupLayerKeys group.visible + layer.visible dono dekhta hai,
      //     aur POPUP_EXCLUDED_LAYERS ko hata deta hai)
      //  3. neeche source ke LAYERS param se cross-check (jo actually render ho
      //     raha hai) — taaki unchecked layer kabhi query na ho
      // Saath hi WMS layer name par dedupe karo.
      const seenWmsNames = new Set();
      const visibleLayers = Object.entries(wmsLayersRef.current).filter(
        ([key, layer]) => {
          if (!layer.getVisible() || !popupLayerKeysRef.current.has(key))
            return false;
          const wmsName = layerExtentsRef.current[key];
          if (!wmsName || seenWmsNames.has(wmsName)) return false;
          seenWmsNames.add(wmsName);
          return true;
        },
      );

      if (visibleLayers.length === 0) {
        setPopupOpen(false);
        setAllPopupFeatures([]);
        overlayRef.current?.setPosition(undefined);
        return;
      }

      const mapInst = mapInstanceRef.current;
      if (!mapInst) {
        setPopupOpen(false);
        setAllPopupFeatures([]);
        overlayRef.current?.setPosition(undefined);
        return;
      }
      const view = mapInst.getView();
      const resolution = view.getResolution();
      const projection = view.getProjection();
      if (!resolution || !projection) {
        setPopupOpen(false);
        setAllPopupFeatures([]);
        overlayRef.current?.setPosition(undefined);
        return;
      }

      // ✅ Layers ko unke WMS source (endpoint) ke hisaab se group karo. Same
      // GeoServer/source wale saare layers EK hi GetFeatureInfo request mein
      // QUERY_LAYERS=layer1,layer2,... combine ho jaate hain — alag-alag call
      // sirf tab lagti hai jab layer sach mein kisi doosre WMS endpoint se ho.
      // ⚠️ NOTE: is file mein "Map" identifier "ol/Map" (OpenLayers ka map
      // viewport class) import karta hai, JS ka built-in Map collection nahi.
      // Isliye grouping ke liye plain object use karo — `new Map()` yahan
      // galti se ol.Map() ban jata (.has/.set jaisa koi method nahi hota).
      const groupsByUrl = {};

      for (const [key, layer] of visibleLayers) {
        const source = layer.getSource();
        if (!source?.getFeatureInfoUrl) continue;

        const wmsLayerName = layerExtentsRef.current[key];
        if (!wmsLayerName) continue;

        // Make sure this layer actually belongs to this WMS source
        const sourceParams = source.getParams?.() || {};
        const sourceLayers = sourceParams.LAYERS || "";
        const sourceLayerNames = sourceLayers
          .split(",")
          .map((name) => name.trim())
          .filter(Boolean);
        if (
          sourceLayerNames.length > 0 &&
          !sourceLayerNames.includes(wmsLayerName)
        ) {
          continue;
        }

        const sourceUrl =
          (typeof source.getUrls === "function"
            ? source.getUrls()?.[0]
            : source.getUrl?.()) || "default";

        if (!groupsByUrl[sourceUrl]) {
          groupsByUrl[sourceUrl] = { source, entries: [] };
        }
        groupsByUrl[sourceUrl].entries.push({ key, wmsLayerName });
      }

      // Saari (per-endpoint) requests PARALLEL bhejo - fast!
      const results = await Promise.all(
        Object.values(groupsByUrl).map(async ({ source, entries }) => {
          const queryLayers = entries.map((e) => e.wmsLayerName).join(",");

          // Let OpenLayers calculate BBOX, WIDTH, HEIGHT and pixel coordinates.
          const url = source.getFeatureInfoUrl(
            coordinate,
            resolution,
            projection,
            {
              INFO_FORMAT: "application/json",
              // Sirf checked + visible layers hi query hoti hain (upar filter
              // dekho). QUERY_LAYERS aur LAYERS dono same list par rakho:
              // WMS spec ke mutabik QUERY_LAYERS, LAYERS ka subset hona chahiye,
              // aur OL default LAYERS source ke params se uthata hai — isliye
              // dono explicitly set karna zaroori hai.
              QUERY_LAYERS: queryLayers,
              LAYERS: queryLayers,
              // GeoServer FEATURE_COUNT poore request par lagta hai; pehle har
              // layer ki apni request thi (10 each), isliye combined request
              // mein bhi wahi total budget rakho.
              FEATURE_COUNT: 10 * entries.length,
              // Click tolerance — dekho FEATURE_INFO_BUFFER_PX ka comment
              BUFFER: FEATURE_INFO_BUFFER_PX,
              // Identify request ko GWC tile-cache grid par snap mat hone do —
              // exact BBOX/I/J se resolve karao.
              TILED: false,
            }
          );

          if (!url) {
            return [];
          }

          try {
            const controller = new AbortController();

            const timeout = setTimeout(() => {
              controller.abort();
            }, 8000);

            const response = await fetch(url, {
              signal: controller.signal,
            });

            clearTimeout(timeout);

            if (!response.ok) {
              throw new Error(
                `GetFeatureInfo failed: ${response.status} ${response.statusText}`
              );
            }

            const data = await response.json();

            if (!data.features || data.features.length === 0) {
              return [];
            }

            // ✅ CRITICAL: GetFeatureInfo geometry REQUEST ke CRS mein aati hai
            // (yani map ka projection, EPSG:3857) — WFS ki tarah EPSG:4326 mein
            // NAHI. Response khud apna CRS declare karta hai
            // ("urn:ogc:def:crs:EPSG::3857"), usi ko parse karke aage bhejo.
            // Ise 4326 maan lena hi wo bug tha jisse highlight click ki jagah se
            // bilkul alag jagah draw ho raha tha.
            const crsName = data?.crs?.properties?.name;
            const crsMatch =
              typeof crsName === "string" ? crsName.match(/EPSG:+(\d+)/i) : null;
            const responseCrs = crsMatch
              ? `EPSG:${crsMatch[1]}`
              : projection.getCode();

            // Har returned feature ko uske GeoServer typeName se match karo.
            // GeoServer feature.id "<layerNameWithoutWorkspace>.<fid>" format
            // mein deta hai (e.g. "mdpe_pipeline.fid-2fb9a202_...").
            return data.features.map((feature) => {
              let matched = entries.length === 1 ? entries[0] : null;

              if (!matched && typeof feature.id === "string") {
                // prefix match karo (fid mein khud dots ho sakte hain, isliye
                // "last dot se pehle" wala approach reliable nahi hai)
                matched = entries.find((e) => {
                  const shortName = e.wmsLayerName.includes(":")
                    ? e.wmsLayerName.split(":").pop()
                    : e.wmsLayerName;
                  return (
                    feature.id.startsWith(`${shortName}.`) ||
                    feature.id.startsWith(`${e.wmsLayerName}.`)
                  );
                });
              }
              // Match na mile to bhi feature drop mat karo - pehla candidate assign karo
              if (!matched) matched = entries[0];

              return {
                layerName:
                  layerNamesRef.current[matched.key] ||
                  layerExtentsRef.current[matched.key] ||
                  matched.key,
                properties: feature.properties,
                // ✅ Highlight ke liye yehi geometry directly use hogi - alag se
                // WFS call nahi lagegi
                geometry: feature.geometry,
                geometryCrs: responseCrs,
                layerKey: matched.key,
              };
            });
          } catch (error) {
            if (error.name === "AbortError") {
              console.warn(`GetFeatureInfo timeout for layers: ${queryLayers}`);
            } else {
              console.error(
                `Feature info error for layers ${queryLayers}:`,
                error,
              );
            }

            return [];
          }
        })
      );

      // ✅ DEDUPE: ye GeoServer har feature ko GetFeatureInfo response mein DO
      // baar deta hai (style mein ek se zyada matching rule hone ki wajah se) —
      // live server par verify kiya: har click par 2 returned / 1 unique.
      // Isi wajah se popup mein har feature do baar dikhta tha aur highlight
      // bhi same geometry par do baar draw hoti thi.
      // feature.id yahan identity ke liye bekaar hai (per-request generate hoti
      // hai), isliye layer + geometry + properties par dedupe karo.
      const seenFeatureKeys = new Set();
      const allFeatures = results.flat().filter((f) => {
        const key = JSON.stringify([f.layerKey, f.geometry, f.properties]);
        if (seenFeatureKeys.has(key)) return false;
        seenFeatureKeys.add(key);
        return true;
      });

      if (allFeatures.length > 0) {
        setAllPopupFeatures(allFeatures);
        // Selection layer turant clear karo — neeche geometry se fill hoga
        if (selectionLayerRef.current) {
          selectionLayerRef.current.getSource().clear();
          selectionLayerRef.current.changed();
        }
        setPopupOpen(true);
        setExpandedAttributes(
          allFeatures.length === 1 ? { "feature-0": true } : {},
        );

        // ✅ Popup ko map click pixel position pe le jao
        const mapSize = mapInstanceRef.current.getSize();
        const pixel = mapInstanceRef.current.getPixelFromCoordinate(coordinate);

        // Map container ka bounding rect nikalo
        const mapRect = mapRef.current.getBoundingClientRect();

        // Absolute position calculate karo
        const absX = mapRect.left + pixel[0];
        const absY = mapRect.top + pixel[1];

        // Popup ko right/bottom dikhao by default, edge pe adjust karo
        const popupW = 360;
        const popupH = 320;

        let finalX = absX + 15;
        let finalY = absY + 15;

        // Right edge check
        if (finalX + popupW > window.innerWidth) {
          finalX = absX - popupW - 15;
        }
        // Bottom edge check
        if (finalY + popupH > window.innerHeight) {
          finalY = absY - popupH - 15;
        }

        setPopupPosition({ x: finalX, y: finalY });

        // Overlay ki zaroorat nahi ab
        if (overlayRef.current) {
          overlayRef.current.setPosition(undefined);
        }

        // ✅ SELECTION - GetFeatureInfo response mein jo geometry already aa
        // chuki hai usi se highlight karo - alag se WFS call ki zaroorat nahi
        if (selectionLayerRef.current) {
          const selectionSource = selectionLayerRef.current.getSource();
          selectionSource.clear();
          const geojsonFormat = new GeoJSON();

          const mapProjCode = projection.getCode();

          for (const feature of allFeatures) {
            if (!feature?.geometry || !feature.geometry.coordinates) continue;
            try {
              const olFeature = geojsonFormat.readFeature(
                { type: "Feature", geometry: feature.geometry },
                {
                  // ✅ Response ne jo CRS declare kiya wahi use karo (GetFeatureInfo
                  // request ke CRS mein geometry deta hai, 4326 mein nahi).
                  // Hardcoded "EPSG:4326" ki wajah se 3857 metres ko degrees
                  // samajh kar reproject kiya ja raha tha — isliye highlight
                  // click se bilkul door draw hota tha.
                  dataProjection: feature.geometryCrs || mapProjCode,
                  featureProjection: mapProjCode,
                },
              );
              selectionSource.addFeature(olFeature);
            } catch (e) {
              console.error("Geometry parse error:", e);
            }
          }
          selectionLayerRef.current.changed();
        }
      } else {
        setPopupOpen(false);
        setAllPopupFeatures([]);
        overlayRef.current?.setPosition(undefined);
        // Empty area click pe selection clear karo
        if (selectionLayerRef.current) {
          selectionLayerRef.current.getSource().clear();
          selectionLayerRef.current.changed();
        }
      }
    };

    map.on("singleclick", handleSingleClick);

    return () => {
      clearTimeout(updateSizeTimer);
      map.un("moveend", handleMoveEnd);
      map.un("singleclick", handleSingleClick);
      map.setTarget(null);
      map.dispose();
      mapInstanceRef.current = null;
      overlayRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!mapRef.current || !mapInstanceRef.current) return;
    const ro = new ResizeObserver(() => mapInstanceRef.current?.updateSize());
    ro.observe(mapRef.current);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    selectedRowsRef.current = selectedRows;
  }, [selectedRows]);
  useEffect(() => {
    layerGroupsRef.current = layerGroups;
    popupLayerKeysRef.current = buildPopupLayerKeys(
      layerGroups,
      POPUP_EXCLUDED_LAYERS.current,
    );
  }, [layerGroups]);

  // Update coordinate format listener
  useEffect(() => {
    if (!mapInstanceRef.current) return;

    const map = mapInstanceRef.current;

    const handlePointerMove = (evt) => {
      const coords = toLonLat(evt.coordinate);
      if (coordinatesDivRef.current) {
        const x =
          coordFormat === "latlon"
            ? coords[1].toFixed(5)
            : coords[0].toFixed(5);
        const y =
          coordFormat === "latlon"
            ? coords[0].toFixed(5)
            : coords[1].toFixed(5);
        coordinatesDivRef.current.setAttribute("data-x", x);
        coordinatesDivRef.current.setAttribute("data-y", y);
        coordinatesDivRef.current.querySelector("#coord-x").textContent =
          coordFormat === "latlon" ? `${x}°N` : `X: ${x}`;
        coordinatesDivRef.current.querySelector("#coord-y").textContent =
          coordFormat === "latlon" ? `${y}°E` : `Y: ${y}`;
      }
    };

    map.on("pointermove", handlePointerMove);

    return () => {
      map.un("pointermove", handlePointerMove);
    };
  }, [coordFormat]);

  const handleZoomIn = () => {
    const view = mapInstanceRef.current?.getView();
    if (view) {
      view.animate({ zoom: view.getZoom() + 1, duration: 250 });
    }
  };

  const handleZoomOut = () => {
    const view = mapInstanceRef.current?.getView();
    if (view) {
      view.animate({ zoom: view.getZoom() - 1, duration: 250 });
    }
  };

  const handleHome = () => {
    const view = mapInstanceRef.current?.getView();
    if (view) {
      view.animate({
        center: fromLonLat([76.993869, 28.448841]),
        zoom: 10,
        duration: 500,
      });
    }
  };

  // ---------------- Print (new module) ----------------
  const printCreatedBy = authUser?.full_name || authUser?.username || "";

  /**
   * Print legend = EXACTLY wahi layers jinka checkbox Layers panel mein ON hai.
   * Group header ka checkbox NAHI dekha jaata — kai groups default mein
   * group-level visible:false hote hain (BurialDepth, OffsetPoints, ...) aur
   * user unke andar ki layer alag se on karta hai; panel use checked dikhata
   * hai, isliye legend mein bhi aani chahiye. Nested ke liye nested checkbox.
   * Legend image ka URL Legend tab wala hi hai.
   */
  const buildPrintLegendGroups = () => {
    const legendUrl = (wmsUrl, wmsLayer) =>
      `${wmsUrl || GEOSERVER_CONFIG.wmsUrl}?REQUEST=GetLegendGraphic&VERSION=1.0.0&FORMAT=image/png&LAYER=${wmsLayer}&scale=50`;
    const groups = [];
    // layerGroups STATE — wahi object jisse Layers panel apne checkboxes render
    // karta hai (handlePrint har render par naya banta hai aur toolCtx deps mein
    // layerGroups hai, isliye ye hamesha current hota hai)
    Object.values(layerGroups).forEach((group) => {
      const layers = [];
      Object.values(group.children || {}).forEach((layer) => {
        if (layer.children) {
          Object.values(layer.children).forEach((nested) => {
            if (nested.visible && nested.wmsLayer) {
              layers.push({
                name: nested.name,
                legendUrl: legendUrl(nested.wmsUrl || layer.wmsUrl, nested.wmsLayer),
              });
            }
          });
        } else if (layer.visible && layer.wmsLayer) {
          layers.push({
            name: layer.name,
            legendUrl: legendUrl(layer.wmsUrl, layer.wmsLayer),
          });
        }
      });
      if (layers.length) groups.push({ name: group.name, layers });
    });
    return groups;
  };

  const BASEMAP_ATTRIBUTION = {
    osm: "© OpenStreetMap contributors",
    worldNavigation: "© Esri",
    worldStreetNight: "© CARTO, © OpenStreetMap contributors",
    imageryLabels: "© Esri, Maxar, Earthstar Geographics",
    topographic: "© OpenTopoMap (CC-BY-SA), © OpenStreetMap contributors",
  };

  const handlePrint = async () => {
    const map = mapInstanceRef.current;
    if (!map) return;
    const t0 = Date.now();
    const log = (m) =>
      setPrintLogs((prev) => [...prev, { t: Date.now() - t0, m }]);
    setPrintLogs([]);
    setPrintError("");
    setPrintProgress(null);
    setPrintGenerating(true);
    try {
      const legendGroups = buildPrintLegendGroups();
      log(`Start — ${printDpi} dpi, A4 landscape`);
      const mapImage = await exportMapImage(map, {
        dpi: printDpi,
        onProgress: setPrintProgress,
        onLog: log,
      });
      const layerCount = legendGroups.reduce((n, g) => n + g.layers.length, 0);
      log(`Legend: ${layerCount} layer(s) in ${legendGroups.length} group(s)`);
      const now = new Date();
      const pad = (n) => String(n).padStart(2, "0");
      const generatedAt = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}`;
      const html = buildPrintHtml({
        title: printTitle.trim() || "Map",
        subtitle: "SKN – Haryana City Gas Distribution · GIS Portal",
        createdBy: printCreatedBy,
        generatedAt,
        logoUrl: new URL(
          `${process.env.PUBLIC_URL || ""}/assets/HCG-SKN-logo.jpg`,
          window.location.href,
        ).href,
        mapImage,
        legendGroups,
        dpi: printDpi,
        basemapName: baseMaps[currentBaseMap]?.name || "",
        basemapAttribution: BASEMAP_ATTRIBUTION[currentBaseMap] || "",
        projection: `${map.getView().getProjection().getCode() === "EPSG:3857" ? "WGS 84 / Pseudo-Mercator" : map.getView().getProjection().getCode()} (${map.getView().getProjection().getCode()})`,
      });
      await printInIframe(html, { onLog: log });
      log(`Done in ${((Date.now() - t0) / 1000).toFixed(1)} s`);
    } catch (e) {
      console.error("Print error:", e);
      log(`✖ ${e?.message || "failed"}`);
      setPrintError(e?.message || "Could not prepare the map for printing");
    } finally {
      setPrintGenerating(false);
      setPrintProgress(null);
    }
  };
  // ---------------- Print end ----------------

  const createWMSLayers = () => {
    const layers = [];

    Object.entries(layerGroups).forEach(([groupKey, group]) => {
      const byUrl = {};

      const collectGroupLayers = (children) => {
        Object.entries(children).forEach(([key, layer]) => {
          if (layer.children) {
            collectGroupLayers(layer.children);
          } else if (layer.wmsLayer) {
            const url = layer.wmsUrl || GEOSERVER_CONFIG.wmsUrl;
            if (!byUrl[url]) byUrl[url] = [];
            byUrl[url].push({
              key,
              layerName: layer.wmsLayer,
              friendlyName: layer.name,
              visible: layer.visible,
            });
          }
        });
      };

      collectGroupLayers(group.children);

      Object.entries(byUrl).forEach(([url, groupLayers]) => {
        // Saare layers ek hi TileLayer mein
        const allLayerNames = groupLayers
          .map((info) => info.layerName)
          .join(",");
        const visibleLayerNames = groupLayers
          .filter((i) => i.visible)
          .map((i) => i.layerName)
          .join(",");

        const tileLayer = new TileLayer({
          source: new TileWMS({
            url,
            params: {
              LAYERS: allLayerNames,
              FORMAT: "image/png8",
              TRANSPARENT: true,
              TILED: true,
            },
            serverType: "geoserver",
            crossOrigin: "anonymous",
            transition: 0,
            tileLoadFunction: tileLoadWithTimeout,
          }),
          visible: !!visibleLayerNames,
          preload: 1,
        });

        groupLayers.forEach((info) => {
          wmsLayersRef.current[info.key] = tileLayer;
          layerExtentsRef.current[info.key] = info.layerName;
          layerNamesRef.current[info.key] = info.friendlyName;
        });

        layers.push(tileLayer);
      });
    });

    return layers;
  };

  const toggleLayerVisibility = useCallback((groupKey, layerKey, nestedKey = null) => {
    const targetKey = nestedKey || layerKey;
    startTransition(() => {
      setLayerGroups((prev) => {
        const next = toggleLayerInGroups(prev, groupKey, layerKey, nestedKey);
        requestAnimationFrame(() => {
          syncTileLayerForKey(
            next,
            targetKey,
            groupKey,
            wmsLayersRef,
            layerExtentsRef,
          );
        });
        return next;
      });
    });
  }, []);

  const toggleGroupVisibility = useCallback((groupKey) => {
    startTransition(() => {
      setLayerGroups((prev) => {
        const next = toggleGroupInGroups(prev, groupKey);
        requestAnimationFrame(() => {
          syncGroupTileLayers(next, groupKey, wmsLayersRef, layerExtentsRef);
        });
        return next;
      });
    });
  }, []);

  const toggleGroupExpanded = useCallback((groupKey) => {
    startTransition(() => {
      setLayerGroups((prev) => ({
        ...prev,
        [groupKey]: {
          ...prev[groupKey],
          expanded: !prev[groupKey].expanded,
        },
      }));
    });
  }, []);

  const toggleNestedExpanded = useCallback((groupKey, layerKey) => {
    startTransition(() => {
      setLayerGroups((prev) =>
        toggleNestedExpandedInGroups(prev, groupKey, layerKey),
      );
    });
  }, []);

  const handleLayerMenuClick = (
    event,
    groupKey,
    layerKey,
    nestedKey = null,
  ) => {
    event.stopPropagation();
    setLayerMenuAnchor(event.currentTarget);
    setSelectedLayer({ groupKey, layerKey, nestedKey });
  };

  const handleLayerMenuClose = () => {
    setLayerMenuAnchor(null);
    setSelectedLayer(null);
  };

  const handleZoomToLayer = async () => {
    const { groupKey, layerKey, nestedKey } = selectedLayer;
    const targetKey = nestedKey || layerKey;
    const wmsLayerName = layerExtentsRef.current[targetKey];

    if (!wmsLayerName) {
      alert("Layer information not found");
      handleLayerMenuClose();
      return;
    }

    try {
      // ✅ YE NAYA CODE
      const layer = layerGroups[groupKey].children[layerKey];
      const actualLayer = nestedKey ? layer.children[nestedKey] : layer;
      const wfsUrl = GEOSERVER_CONFIG.getWfsUrlForLayer(actualLayer.wmsUrl);
      const params = new URLSearchParams({
        ...DEFAULT_WFS_PARAMS,
        typeName: wmsLayerName,
        // maxFeatures: 1000,
      });
      const response = await fetch(`${wfsUrl}?${params}`);
      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);

      const data = await response.json();

      // ✅ DEBUGGING CONSOLE (optional, baad me remove kar sakte ho)
      console.log("WFS Response:", data);

      if (!data.features || data.features.length === 0) {
        alert("No features found in this layer");
        handleLayerMenuClose();
        return;
      }

      // ✅ Check if all features have valid geometries
      const validFeatures = data.features.filter(
        (f) => f.geometry && f.geometry.coordinates,
      );
      if (validFeatures.length === 0) {
        alert("Layer has no valid geometries");
        handleLayerMenuClose();
        return;
      }

      const geojsonFormat = new GeoJSON();
      const features = geojsonFormat.readFeatures(data, {
        dataProjection: "EPSG:4326",
        featureProjection: "EPSG:3857",
      });

      // ✅ Calculate combined extent with better handling
      let combinedExtent = null;
      let pointCount = 0;
      let pointCenter = null;

      features.forEach((feature) => {
        const geometry = feature.getGeometry();
        if (geometry) {
          const geomType = geometry.getType();

          // Special handling for Point geometries
          if (geomType === "Point") {
            pointCount++;
            pointCenter = geometry.getCoordinates();
          }

          const featureExtent = geometry.getExtent();

          // Check if extent has real values
          if (featureExtent.every((val) => isFinite(val))) {
            if (!combinedExtent) {
              combinedExtent = [...featureExtent];
            } else {
              combinedExtent[0] = Math.min(combinedExtent[0], featureExtent[0]);
              combinedExtent[1] = Math.min(combinedExtent[1], featureExtent[1]);
              combinedExtent[2] = Math.max(combinedExtent[2], featureExtent[2]);
              combinedExtent[3] = Math.max(combinedExtent[3], featureExtent[3]);
            }
          }
        }
      });

      const view = mapInstanceRef.current.getView();

      // ✅ If only points and extent is too small, use center + zoom
      if (
        pointCount > 0 &&
        (!combinedExtent || !isValidExtent(combinedExtent))
      ) {
        view.animate({
          center: pointCenter,
          zoom: 16,
          duration: 800,
        });
      } else if (combinedExtent && isValidExtent(combinedExtent)) {
        // Check if extent is too small (might be single point)
        const [minX, minY, maxX, maxY] = combinedExtent;
        const width = maxX - minX;
        const height = maxY - minY;

        if (width < 10 && height < 10) {
          // Very small extent, use center + fixed zoom
          const centerX = (minX + maxX) / 2;
          const centerY = (minY + maxY) / 2;
          view.animate({
            center: [centerX, centerY],
            // zoom: 16,
            duration: 800,
          });
        } else {
          // Normal extent fit
          view.fit(combinedExtent, {
            padding: [50, 50, 50, 50],
            duration: 800,
            // maxZoom: 18,
          });
        }
      } else {
        alert("Unable to calculate valid extent");
        handleLayerMenuClose();
        return;
      }
    } catch (error) {
      console.error("Zoom error:", error);
      alert(`Unable to zoom: ${error.message}`);
    }
    handleLayerMenuClose();
  };

  // Zoom to specific feature - Fixed with error handling
  const handleZoomToFeature = async (featureData) => {
    if (!mapInstanceRef.current) return;

    const geojsonFormat = new GeoJSON();
    const wmsLayerName = layerExtentsRef.current[featureData.layerKey];
    if (!wmsLayerName || !featureData.properties) return;

    const zoomAndSelect = (olFeature) => {
      isZoomingToFeatureRef.current = true;
      const geometry = olFeature.getGeometry();
      if (!geometry) return;

      const view = mapInstanceRef.current.getView();

      if (selectionLayerRef.current) {
        selectionLayerRef.current.getSource().clear();
        selectionLayerRef.current.getSource().addFeature(olFeature);
        selectionLayerRef.current.changed();
      }

      const extent = geometry.getExtent();
      if (extent && extent.every((v) => isFinite(v))) {
        const [minX, minY, maxX, maxY] = extent;
        const width = maxX - minX;
        const height = maxY - minY;

        if (width < 1 && height < 1) {
          // Point — current zoom maintain karo, sirf center pe jao
          const currentZoom = view.getZoom();
          view.animate({
            center: [(minX + maxX) / 2, (minY + maxY) / 2],
            zoom: Math.max(currentZoom, 17), // ← zoom out mat karo, sirf zoom in karo minimum 17 tak
            duration: 800,
          });
        } else {
          // Line / Polygon
          view.fit(extent, {
            padding: [80, 80, 80, 80],
            duration: 800,
          });
        }
      }
    };

    const mapProjCode =
      mapInstanceRef.current?.getView().getProjection().getCode() ||
      "EPSG:3857";

    // ✅ PRIMARY: GetFeatureInfo ne jo geometry di hai wahi use karo — ye
    // THEEK USI feature ki hai jispe user ne click kiya tha. Na koi extra
    // request, na koi guessing.
    //
    // ⚠️ Pehle yahan "ALWAYS WFS se fetch karo" wala approach tha jo pehle ek
    // ID field guess karta tha. Jis layer mein objectid/gid/fid/id nahi hota
    // (jaise mdpe_pipeline — uske fields sirf Dia/UOM/Length_M/... hain), wahan
    // wo "pehla numeric field" utha leta tha, yani Dia (diameter = 63). Fir
    // `CQL_FILTER: Dia = 63` chalta tha jo 940 features match karta hai, aur
    // maxFeatures:1 unmein se koi bhi random pipeline laut ata tha — isliye lens
    // icon click karne par map kisi aur hi pipeline par chala jaata tha.
    if (featureData?.geometry && featureData.geometry.coordinates) {
      try {
        const olFeature = geojsonFormat.readFeature(
          { type: "Feature", geometry: featureData.geometry },
          {
            // GetFeatureInfo geometry request ke CRS mein aati hai (map
            // projection), WFS wali EPSG:4326 mein nahi
            dataProjection: featureData.geometryCrs || mapProjCode,
            featureProjection: mapProjCode,
          },
        );
        zoomAndSelect(olFeature);
        return;
      } catch (e) {
        console.warn("Geometry zoom error:", e);
      }
    }

    // Geometry hi nahi mili to kuch mat karo — ID-based WFS lookup ab
    // deliberately hata diya gaya hai (dekho upar wala comment).
    console.warn(
      "Zoom: is feature ke saath koi geometry nahi aayi —",
      wmsLayerName,
    );
  };

  const handleZoomToSelected = () => {
    if (selectedRows.length === 0) {
      alert("Please select at least one row");
      return;
    }
    if (!mapInstanceRef.current) return;

    // __rowId se features match karo
    const selectedFeatures = attributeFeaturesRef.current.filter((_, idx) =>
      selectedRows.includes(idx),
    );

    if (selectedFeatures.length === 0) {
      alert("No geometry data found. Please reload the table.");
      return;
    }

    const geojsonFormat = new GeoJSON();

    // Valid geometry wale features hi lo
    const validFeatures = selectedFeatures.filter(
      (f) => f.geometry && f.geometry.coordinates,
    );

    if (validFeatures.length === 0) {
      alert("Selected features have no geometry.");
      return;
    }

    const olFeatures = geojsonFormat.readFeatures(
      { type: "FeatureCollection", features: validFeatures },
      { dataProjection: "EPSG:4326", featureProjection: "EPSG:3857" },
    );

    // Selection layer update karo
    if (selectionLayerRef.current) {
      selectionLayerRef.current.getSource().clear();
      selectionLayerRef.current.getSource().addFeatures(olFeatures);
      selectionLayerRef.current.changed();
    }

    // Extent calculate karo
    let combinedExtent = null;
    olFeatures.forEach((f) => {
      const geom = f.getGeometry();
      if (!geom) return;
      const ext = geom.getExtent();
      if (ext && ext.every((v) => isFinite(v))) {
        if (!combinedExtent) {
          combinedExtent = [...ext];
        } else {
          combinedExtent[0] = Math.min(combinedExtent[0], ext[0]);
          combinedExtent[1] = Math.min(combinedExtent[1], ext[1]);
          combinedExtent[2] = Math.max(combinedExtent[2], ext[2]);
          combinedExtent[3] = Math.max(combinedExtent[3], ext[3]);
        }
      }
    });

    if (!combinedExtent || !isValidExtent(combinedExtent)) {
      alert("No valid geometry to zoom to.");
      return;
    }

    const view = mapInstanceRef.current.getView();
    const [minX, minY, maxX, maxY] = combinedExtent;
    const width = maxX - minX;
    const height = maxY - minY;

    // Point ya bahut chota extent
    if (width < 1 && height < 1) {
      const currentZoom = view.getZoom();
      view.animate({
        center: [(minX + maxX) / 2, (minY + maxY) / 2],
        zoom: Math.max(currentZoom, 17),
        duration: 800,
      });
    } else {
      view.fit(combinedExtent, {
        padding: [80, 80, 80, 80],
        duration: 800,
      });
    }
  };

  // Docked table resize handlers
  const handleDockedResizeStart = (e) => {
    e.preventDefault();
    setIsResizingDocked(true);
  };

  const handleDockedResizeMove = (e) => {
    if (isResizingDocked) {
      const windowHeight = window.innerHeight;
      const mouseY = e.clientY;
      const bottomBarHeight = 48; // Bottom scale bar height
      const minHeight = 150;

      // Calculate new height from bottom
      const newHeight = windowHeight - mouseY - bottomBarHeight;

      if (newHeight >= minHeight && newHeight <= windowHeight - 200) {
        setDockedTableHeight(newHeight);
      }
    }
  };

  const handleDockedResizeEnd = () => {
    setIsResizingDocked(false);
  };

  useEffect(() => {
    if (isResizingDocked) {
      document.addEventListener("mousemove", handleDockedResizeMove);
      document.addEventListener("mouseup", handleDockedResizeEnd);
    } else {
      document.removeEventListener("mousemove", handleDockedResizeMove);
      document.removeEventListener("mouseup", handleDockedResizeEnd);
    }
    return () => {
      document.removeEventListener("mousemove", handleDockedResizeMove);
      document.removeEventListener("mouseup", handleDockedResizeEnd);
    };
  }, [isResizingDocked]);

  const handleOpenAttributeTable = async () => {
    const { groupKey, layerKey, nestedKey } = selectedLayer;
    const targetKey = nestedKey || layerKey;
    const wmsLayerName = layerExtentsRef.current[targetKey];

    if (!wmsLayerName) {
      alert("Layer information not found");
      handleLayerMenuClose();
      return;
    }

    // ✅ Agar is layer ka query data already saved hai to wahi use karo
    if (
      selectedQueryLayer === targetKey &&
      attributeData.length > 0 &&
      attributeLayerName.includes(wmsLayerName)
    ) {
      // Query result already available - seedha table open karo
      setAttributeTableOpen(true);
      setAttributeTableDocked(true);
      setTablePage(0);
      handleLayerMenuClose();
      return;
    }

    // Nahi hai to fresh WFS fetch karo (existing code)
    setAttributeTableOpen(true);
    setAttributeLoading(true);
    setAttributeLayerName(layerNamesRef.current[targetKey] || wmsLayerName);
    handleLayerMenuClose();

    try {
      const params = new URLSearchParams({
        ...DEFAULT_WFS_PARAMS,
        typeName: wmsLayerName,
      });
      const response = await fetch(`${GEOSERVER_CONFIG.wfsUrl}?${params}`);
      const data = await response.json();

      if (data.features && data.features.length > 0) {
        const firstFeature = data.features[0].properties;
        const cols = Object.keys(firstFeature).map((key) => ({
          id: key,
          label: key.toUpperCase().replace(/_/g, " "),
        }));
        const rows = data.features.map((feature, index) => ({
          ...feature.properties,
          __rowId: index,
        }));
        attributeFeaturesRef.current = data.features;
        setAttributeColumns(cols);
        setAttributeData(rows);
        setTablePage(0);
      } else {
        setAttributeColumns([]);
        setAttributeData([]);
        alert("No features found in this layer");
      }
    } catch (error) {
      console.error("Attribute table error:", error);
      alert("Unable to load attribute table.");
      setAttributeTableOpen(false);
    } finally {
      setAttributeLoading(false);
    }
  };

  const handleMyLocation = () => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser.");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const view = mapInstanceRef.current?.getView();
        const lon = position.coords.longitude;
        const lat = position.coords.latitude;

        if (view) {
          view.animate({
            center: fromLonLat([lon, lat]),
            zoom: 15,
            duration: 500,
          });
        }

        // Add blue marker at current location
        if (markerLayerRef.current) {
          const vectorSource = markerLayerRef.current.getSource();
          vectorSource.clear();

          const marker = new Feature({
            geometry: new Point(fromLonLat([lon, lat])),
          });

          // Create blue marker for current location
          const markerStyle = new Style({
            image: new Icon({
              anchor: [0.5, 0.5],
              anchorXUnits: "fraction",
              anchorYUnits: "fraction",
              src:
                "data:image/svg+xml;charset=utf-8," +
                encodeURIComponent(`
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10" fill="#2196F3" stroke="white" stroke-width="3"/>
                  <circle cx="12" cy="12" r="4" fill="white"/>
                </svg>
              `),
              scale: 1,
            }),
          });

          marker.setStyle(markerStyle);
          vectorSource.addFeature(marker);
        }
      },
      (error) => {
        console.error("Geolocation error:", error);
        let errorMessage = "Unable to get your location. ";

        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage +=
              "Please enable location permission in your browser settings and try again.";
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage +=
              "Location information is unavailable. Please check your GPS/network connection.";
            break;
          case error.TIMEOUT:
            errorMessage += "Location request timed out. Please try again.";
            break;
          default:
            errorMessage += "An unknown error occurred.";
        }

        alert(errorMessage);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      },
    );
  };

  const changeBaseMap = (mapType) => {
    if (mapInstanceRef.current && baseLayerRef.current) {
      const map = mapInstanceRef.current;
      const layers = map.getLayers();

      layers.removeAt(0);

      const newBaseLayer = baseMaps[mapType].layer();
      layers.insertAt(0, newBaseLayer);
      baseLayerRef.current = newBaseLayer;

      setCurrentBaseMap(mapType);
      setBaseMapMenuAnchor(null);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
          searchQuery,
        )}`,
      );
      const data = await response.json();

      if (data && data.length > 0) {
        const result = data[0];
        goToLocation(result);
        setShowSuggestions(false);
      }
    } catch (error) {
      console.error("Search error:", error);
    }
  };

  const handleSearchInput = async (value) => {
    setSearchQuery(value);

    if (value.trim().length > 2) {
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
            value,
          )}&limit=5`,
        );
        const data = await response.json();
        setSearchResults(data);
        setShowSuggestions(true);
      } catch (error) {
        console.error("Search error:", error);
      }
    } else {
      setSearchResults([]);
      setShowSuggestions(false);
    }
  };

  const goToLocation = (result) => {
    const view = mapInstanceRef.current?.getView();
    const lon = parseFloat(result.lon);
    const lat = parseFloat(result.lat);

    if (view) {
      view.animate({
        center: fromLonLat([lon, lat]),
        zoom: 15,
        duration: 500,
      });
    }

    // Add marker at searched location
    if (markerLayerRef.current) {
      const vectorSource = markerLayerRef.current.getSource();
      vectorSource.clear(); // Clear previous markers

      const marker = new Feature({
        geometry: new Point(fromLonLat([lon, lat])),
      });

      // Create marker style with SVG
      const markerStyle = new Style({
        image: new Icon({
          anchor: [0.5, 1],
          anchorXUnits: "fraction",
          anchorYUnits: "fraction",
          src:
            "data:image/svg+xml;charset=utf-8," +
            encodeURIComponent(`
            <svg xmlns="http://www.w3.org/2000/svg" width="40" height="52" viewBox="0 0 40 52">
              <path fill="red" stroke="white" stroke-width="3" d="M20,0 C9,0 0,9 0,20 C0,35 20,52 20,52 C20,52 40,35 40,20 C40,9 31,0 20,0 Z"/>
              <circle cx="20" cy="20" r="8" fill="white"/>
            </svg>
          `),
          scale: 0.8,
        }),
      });

      marker.setStyle(markerStyle);
      vectorSource.addFeature(marker);
    }

    setSearchQuery(result.display_name);
    setShowSuggestions(false);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };
  // ADD THESE HANDLERS
  const handleMouseDown = () => {
    setIsResizing(true);
  };

  const handleMouseMove = (e) => {
    if (isResizing) {
      const newWidth = e.clientX;
      if (newWidth >= DRAWER_MIN_WIDTH && newWidth <= DRAWER_MAX_WIDTH) {
        setDrawerWidth(newWidth);
      }
    }
  };

  const handleMouseUp = () => {
    setIsResizing(false);
  };

  // ---------------- Bookmarks (server, user-wise, paged) ----------------
  const loadBookmarks = useCallback(async (page = 1) => {
    setBookmarkLoading(true);
    setBookmarkError("");
    try {
      const data = await listBookmarks({ page, pageSize: BOOKMARK_PAGE_SIZE });
      // Agar page range se bahar ho gaya (e.g. last item delete) to peeche jao
      if (data.pages > 0 && page > data.pages) {
        return loadBookmarks(data.pages);
      }
      setBookmarks(data.items || []);
      setBookmarkPage(data.page);
      setBookmarkPages(data.pages);
      setBookmarkTotal(data.total);
    } catch (e) {
      setBookmarkError(e.message || "Could not load bookmarks");
    } finally {
      setBookmarkLoading(false);
    }
  }, []);

  // Bookmark tool khulte hi list load karo
  useEffect(() => {
    if (activeTools.includes("bookmark")) {
      loadBookmarks(1);
    }
  }, [activeTools, loadBookmarks]);

  const handleSaveBookmark = async () => {
    const name = bookmarkName.trim();
    if (!name) {
      setBookmarkError("Please enter a bookmark name");
      return;
    }
    const view = mapInstanceRef.current?.getView();
    if (!view) return;
    const [center_lon, center_lat] = toLonLat(view.getCenter());

    setBookmarkLoading(true);
    setBookmarkError("");
    try {
      await createBookmark({
        name,
        center_lon,
        center_lat,
        zoom: view.getZoom(),
      });
      setBookmarkName("");
      // Naya bookmark sabse upar aata hai (created desc) -> page 1 dikhao
      await loadBookmarks(1);
    } catch (e) {
      // 409 duplicate ya validation message backend se aata hai
      setBookmarkError(e.message || "Could not save bookmark");
      setBookmarkLoading(false);
    }
  };

  const handleGoToBookmark = (bookmark) => {
    const view = mapInstanceRef.current?.getView();
    if (view) {
      view.animate({
        center: fromLonLat([bookmark.center_lon, bookmark.center_lat]),
        zoom: bookmark.zoom,
        duration: 500,
      });
    }
  };

  const handleDeleteBookmark = async (bookmarkId) => {
    setBookmarkLoading(true);
    setBookmarkError("");
    try {
      await deleteBookmark(bookmarkId);
      await loadBookmarks(bookmarkPage);
    } catch (e) {
      setBookmarkError(e.message || "Could not delete bookmark");
      setBookmarkLoading(false);
    }
  };
  // ---------------- Bookmarks end ----------------
  const updateMapSelectionFromRows = useCallback((newSelectedRows) => {
    if (!selectionLayerRef.current) return;
    const selSource = selectionLayerRef.current.getSource();
    selSource.clear();
    if (newSelectedRows.length === 0) {
      selectionLayerRef.current.changed();
      return;
    }
    const selectedFeats = attributeFeaturesRef.current.filter((_, idx) =>
      newSelectedRows.includes(idx),
    );
    if (selectedFeats.length === 0) return;
    const geojsonFormat = new GeoJSON();
    try {
      const olFeatures = geojsonFormat.readFeatures(
        { type: "FeatureCollection", features: selectedFeats },
        { dataProjection: "EPSG:4326", featureProjection: "EPSG:3857" },
      );
      selSource.addFeatures(olFeatures);
      selectionLayerRef.current.changed();
    } catch (e) {
      console.error("Selection update error:", e);
    }
  }, []); // ← empty dependency

  const fetchLayerFields = async (layerKey) => {
    const wmsLayerName = layerExtentsRef.current[layerKey];
    if (!wmsLayerName) return;
    try {
      const params = new URLSearchParams({
        ...DEFAULT_WFS_PARAMS,
        typeName: wmsLayerName,
        maxFeatures: 1, // ← SIRF 1 FEATURE CHAHIYE FIELDS KE LIYE
      });
      const res = await fetch(`${GEOSERVER_CONFIG.wfsUrl}?${params}`);
      const data = await res.json();
      if (data.features?.length > 0) {
        setQueryLayerFields(Object.keys(data.features[0].properties));
      }
    } catch (e) {
      console.error(e);
    }
  };

  const fetchFieldValues = async (layerKey, fieldName, clauseId) => {
    const wmsLayerName = layerExtentsRef.current[layerKey];
    if (!wmsLayerName || !fieldName) return;
    setQueryFieldValuesLoading((prev) => ({ ...prev, [clauseId]: true }));
    try {
      const params = new URLSearchParams({
        ...DEFAULT_WFS_PARAMS,
        typeName: wmsLayerName,
        maxFeatures: 500,
        propertyName: fieldName,
      });
      const res = await fetch(`${GEOSERVER_CONFIG.wfsUrl}?${params}`);
      const data = await res.json();
      if (data.features?.length > 0) {
        const unique = [
          ...new Set(
            data.features
              .map((f) => f.properties[fieldName])
              .filter((v) => v !== null && v !== undefined && v !== "")
              .map(String),
          ),
        ].sort();
        setQueryFieldValues((prev) => ({ ...prev, [clauseId]: unique }));
      }
    } catch (e) {
      console.error(e);
    } finally {
      setQueryFieldValuesLoading((prev) => ({ ...prev, [clauseId]: false }));
    }
  };

  const handleApplyQuery = async () => {
    if (!queryLayer) {
      alert("Please select a layer");
      return;
    }

    const wmsLayerName = layerExtentsRef.current[queryLayer];
    if (!wmsLayerName) return;

    // CQL Filter build karo
    const conditions = queryClauses
      .filter(
        (c) =>
          c.field &&
          (c.operator === "is blank" ||
            c.operator === "is not blank" ||
            c.value !== ""),
      )
      .map((c, idx) => {
        let condition = "";
        switch (c.operator) {
          case "is equal to":
            condition = `${c.field} = '${c.value}'`;
            break;
          case "is not equal to":
            condition = `${c.field} <> '${c.value}'`;
            break;
          case "is greater than":
            condition = `${c.field} > '${c.value}'`;
            break;
          case "is less than":
            condition = `${c.field} < '${c.value}'`;
            break;
          case "contains":
            condition = `${c.field} LIKE '%${c.value}%'`;
            break;
          case "starts with":
            condition = `${c.field} LIKE '${c.value}%'`;
            break;
          case "is blank":
            condition = `${c.field} IS NULL`;
            break;
          case "is not blank":
            condition = `${c.field} IS NOT NULL`;
            break;
          default:
            condition = `${c.field} = '${c.value}'`;
        }
        if (idx > 0) {
          const conj = c.conjunction === "Or" ? " OR " : " AND ";
          return conj + condition;
        }
        return condition;
      });

    if (conditions.length === 0) {
      alert("Please fill at least one condition");
      return;
    }

    let cqlFilter = conditions.join("");
    if (invertWhere) cqlFilter = `NOT (${cqlFilter})`;

    try {
      const params = new URLSearchParams({
        ...DEFAULT_WFS_PARAMS,
        typeName: wmsLayerName,
        CQL_FILTER: cqlFilter,
      });
      const res = await fetch(`${GEOSERVER_CONFIG.wfsUrl}?${params}`);
      const data = await res.json();

      if (!data.features || data.features.length === 0) {
        alert("No features found matching the query");
        return;
      }

      // ✅ 1. MAP PE HIGHLIGHT karo (selection layer mein)
      if (selectionLayerRef.current) {
        const selSource = selectionLayerRef.current.getSource();
        selSource.clear();
        const geojsonFormat = new GeoJSON();
        const features = geojsonFormat.readFeatures(data, {
          dataProjection: "EPSG:4326",
          featureProjection: "EPSG:3857",
        });
        selSource.addFeatures(features);
        selectionLayerRef.current.changed();

        // ✅ 2. ZOOM TO results
        const extent = selSource.getExtent();
        if (extent && isValidExtent(extent)) {
          const [minX, minY, maxX, maxY] = extent;
          if (maxX - minX < 1 && maxY - minY < 1) {
            mapInstanceRef.current.getView().animate({
              center: [(minX + maxX) / 2, (minY + maxY) / 2],
              zoom: 15,
              duration: 800,
            });
          } else {
            mapInstanceRef.current.getView().fit(extent, {
              padding: [100, 100, 100, 100],
              duration: 800,
            });
          }
        }
      }

      // ✅ 3. Query results ko ref mein save karo
      //    (Attribute table manually open karne pe ye data use hoga)
      const firstFeature = data.features[0].properties;
      const cols = Object.keys(firstFeature).map((key) => ({
        id: key,
        label: key.toUpperCase().replace(/_/g, " "),
      }));
      const rows = data.features.map((feature, index) => ({
        ...feature.properties,
        __rowId: index,
      }));
      // Query results map pe highlight karo
      const queryIds = rows.map((r) => r.id);

      // Ab PURI layer fetch karo (bina filter ke) - ALL tab ke liye
      const allParams = new URLSearchParams({
        ...DEFAULT_WFS_PARAMS,
        typeName: wmsLayerName,
      });
      const allRes = await fetch(`${GEOSERVER_CONFIG.wfsUrl}?${allParams}`);
      const allData = await allRes.json();

      const allRows = allData.features.map((feature, index) => ({
        ...feature.properties,
        __rowId: index,
      }));

      attributeFeaturesRef.current = allData.features;
      setAttributeColumns(cols);
      setAttributeData(allRows); // ← Puri layer ke rows

      // Query result wale rows ko selected mark karo
      // (query features ke properties match karke)
      const queryRowIds = allRows
        .filter((row) =>
          rows.some((qr) =>
            attributeColumns.length > 0
              ? qr[attributeColumns[0]?.id] === row[attributeColumns[0]?.id]
              : false,
          ),
        )
        .map((r) => r.__rowId);

      setAttributeLayerName(
        `${wmsLayerName} (Query: ${data.features.length} results)`,
      );
      setTablePage(0);
      setSelectedRows(queryRowIds);
      updateMapSelectionFromRows(queryRowIds);

      // ✅ 4. Layer panel mein us layer ko visually SELECTED dikhaao
      //    queryLayer state already set hai, use highlight ke liye use karo
      setSelectedQueryLayer(queryLayer); // naya state (neeche add karenge)

      // ✅ 5. Attribute table AUTO-OPEN NAHI hoga
      //    User manually layer menu se "Open Attribute Table" click karega
      //    Tab sirf layers tab pe le jao

      setAttributeTableOpen(true);
      setAttributeTableDocked(true);
      setTableViewMode("all");
    } catch (e) {
      console.error(e);
      alert("Query failed: " + e.message);
    }
  };

  const applyFilterToLayer = (layerKey, cqlFilter) => {
    const wmsLayerName = layerExtentsRef.current[layerKey];
    if (!wmsLayerName) return;

    const olLayer = wmsLayersRef.current[layerKey];
    if (!olLayer) return;

    // Check karo kitne keys is same TileLayer ko share kar rahe hain
    const sharedKeys = Object.keys(wmsLayersRef.current).filter(
      (k) => wmsLayersRef.current[k] === olLayer,
    );

    if (sharedKeys.length === 1) {
      // Sirf ek layer hai - directly CQL_FILTER lagao
      olLayer.getSource().updateParams({ CQL_FILTER: cqlFilter });
      olLayer.getSource().refresh();
    } else {
      // Multiple layers share kar rahe hain - naya alag TileLayer banao
      const layerUrl =
        olLayer.getSource().getUrls?.()?.[0] ||
        GEOSERVER_CONFIG.wmsUrl;

      const newTileLayer = new TileLayer({
        source: new TileWMS({
          url: layerUrl,
          params: {
            LAYERS: wmsLayerName,
            FORMAT: "image/png8",
            TRANSPARENT: true,
            TILED: true,
            CQL_FILTER: cqlFilter,
          },
          serverType: "geoserver",
          crossOrigin: "anonymous",
          transition: 0,
        }),
        visible: true,
        preload: 2,
        zIndex: olLayer.getZIndex() || 1,
      });

      // Original layer se is layerKey ko hatao (baaki layers same TileLayer use karte rahe)
      // Original layer ka LAYERS param update karo (is layer ko hatao)
      const remainingLayerNames = sharedKeys
        .filter((k) => k !== layerKey)
        .map((k) => layerExtentsRef.current[k])
        .join(",");

      if (remainingLayerNames) {
        olLayer.getSource().updateParams({ LAYERS: remainingLayerNames });
      } else {
        olLayer.setVisible(false);
      }

      // Naya layer map pe add karo
      mapInstanceRef.current.addLayer(newTileLayer);

      // Ref update karo
      wmsLayersRef.current[layerKey] = newTileLayer;
    }
  };

  // Format length measurement
  const formatLength = (length, unit) => {
    let output;
    switch (unit) {
      case "meters":
        output =
          length > 1000
            ? `${(length / 1000).toFixed(2)} km`
            : `${length.toFixed(2)} m`;
        break;
      case "kilometers":
        output = `${(length / 1000).toFixed(2)} km`;
        break;
      case "feet":
        output = `${(length * 3.28084).toFixed(2)} ft`;
        break;
      case "miles":
        output = `${(length / 1609.344).toFixed(2)} mi`;
        break;
      default:
        output = `${length.toFixed(2)} m`;
    }
    return output;
  };

  // Format area measurement
  const formatArea = (area, unit) => {
    let output;
    switch (unit) {
      case "sqmeters":
        output =
          area > 10000
            ? `${(area / 1000000).toFixed(2)} km²`
            : `${area.toFixed(2)} m²`;
        break;
      case "sqkilometers":
        output = `${(area / 1000000).toFixed(2)} km²`;
        break;
      case "hectares":
        output = `${(area / 10000).toFixed(2)} ha`;
        break;
      case "acres":
        output = `${(area / 4046.86).toFixed(2)} ac`;
        break;
      default:
        output = `${area.toFixed(2)} m²`;
    }
    return output;
  };

  // Start measurement
  const startMeasurement = () => {
    if (!mapInstanceRef.current) return;

    setIsMeasuring(true);
    const map = mapInstanceRef.current;

    // Create measure layer if not exists
    if (!measureLayerRef.current) {
      const source = new VectorSource();
      const vector = new VectorLayer({
        source: source,
        style: new Style({
          fill: new Fill({
            color: "rgba(255, 165, 0, 0.2)",
          }),
          stroke: new Stroke({
            color: "#ff6600",
            width: 3,
          }),
        }),
        zIndex: 1000,
      });
      measureLayerRef.current = vector;
      map.addLayer(vector);
    }

    // Create draw interaction
    const draw = new Draw({
      source: measureLayerRef.current.getSource(),
      type: measureType,
      style: new Style({
        fill: new Fill({
          color: "rgba(255, 165, 0, 0.2)",
        }),
        stroke: new Stroke({
          color: "#ff6600",
          lineDash: [10, 10],
          width: 3,
        }),
      }),
    });

    drawInteractionRef.current = draw;
    map.addInteraction(draw);

    // Create tooltip overlay for live measurement
    const tooltipElement = document.createElement("div");
    tooltipElement.className = "ol-tooltip ol-tooltip-measure";
    tooltipElement.style.cssText = `
    position: absolute;
    background-color: rgba(0, 0, 0, 0.7);
    color: white;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
    pointer-events: none;
    white-space: nowrap;
  `;

    const measureTooltip = new Overlay({
      element: tooltipElement,
      offset: [0, -15],
      positioning: "bottom-center",
    });
    map.addOverlay(measureTooltip);
    measureOverlayRef.current = measureTooltip;

    let sketch;

    draw.on("drawstart", (evt) => {
      sketch = evt.feature;

      sketch.getGeometry().on("change", (e) => {
        const geom = e.target;
        let output;

        const geomType = geom.getType();

        if (geomType === "Polygon") {
          output = formatArea(getArea(geom), measureAreaUnit);
        } else if (geomType === "LineString") {
          output = formatLength(getLength(geom), measureUnit);
        }

        tooltipElement.innerHTML = output;
        measureTooltip.setPosition(geom.getLastCoordinate());
      });
    });

    draw.on("drawend", (evt) => {
      const geometry = evt.feature.getGeometry();
      let value, type;

      if (geometry.getType() === "Polygon") {
        value = formatArea(getArea(geometry), measureAreaUnit);
        type = "Polygon";
      } else {
        value = formatLength(getLength(geometry), measureUnit);
        type = "LineString";
      }

      // Save measurement
      setMeasurements((prev) => [
        ...prev,
        {
          id: Date.now(),
          type: type,
          value: value,
          unit: type === "LineString" ? measureUnit : measureAreaUnit,
          olFeature: evt.feature, // ← ADD KARO
        },
      ]);

      // Remove draw interaction
      map.removeInteraction(draw);
      map.removeOverlay(measureTooltip);
      setIsMeasuring(false);
      drawInteractionRef.current = null;
    });
  };

  // Stop measurement
  const stopMeasurement = () => {
    if (!mapInstanceRef.current || !drawInteractionRef.current) return;

    mapInstanceRef.current.removeInteraction(drawInteractionRef.current);
    if (measureOverlayRef.current) {
      mapInstanceRef.current.removeOverlay(measureOverlayRef.current);
    }
    setIsMeasuring(false);
    drawInteractionRef.current = null;
  };
  // Point in polygon check (ray casting algorithm)
  const isPointInPolygon = (lon, lat, ring) => {
    let inside = false;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const xi = ring[i][0],
        yi = ring[i][1];
      const xj = ring[j][0],
        yj = ring[j][1];
      if (
        yi > lat !== yj > lat &&
        lon < ((xj - xi) * (lat - yi)) / (yj - yi) + xi
      ) {
        inside = !inside;
      }
    }
    return inside;
  };

  // Check karo feature buffer ke andar hai ya nahi - ALL geometry types handle
  const isFeatureInRadius = (feature, centerLon, centerLat, radiusMeters) => {
    const geom = feature.geometry;
    if (!geom) return false;

    const { latDelta, lonDelta } = metersToDegrees(radiusMeters, centerLat);

    // Point distance check
    const inRadius = (coord) =>
      getDistanceMeters(centerLon, centerLat, coord[0], coord[1]) <=
      radiusMeters;

    // Line segment se minimum distance check karo
    const segmentInRadius = (coords) => {
      for (let i = 0; i < coords.length - 1; i++) {
        const [x1, y1] = coords[i];
        const [x2, y2] = coords[i + 1];
        // Segment pe nearest point dhundo
        const dx = x2 - x1;
        const dy = y2 - y1;
        const lenSq = dx * dx + dy * dy;
        let t = 0;
        if (lenSq > 0) {
          t = ((centerLon - x1) * dx + (centerLat - y1) * dy) / lenSq;
          t = Math.max(0, Math.min(1, t));
        }
        const nearestLon = x1 + t * dx;
        const nearestLat = y1 + t * dy;
        if (
          getDistanceMeters(centerLon, centerLat, nearestLon, nearestLat) <=
          radiusMeters
        ) {
          return true;
        }
      }
      return false;
    };

    switch (geom.type) {
      case "Point":
        return inRadius(geom.coordinates);

      case "MultiPoint":
        return geom.coordinates.some(inRadius);

      case "LineString":
        return segmentInRadius(geom.coordinates);

      case "MultiLineString":
        return geom.coordinates.some(segmentInRadius);

      case "Polygon": {
        const ring = geom.coordinates[0];
        // Buffer center polygon ke andar hai
        if (isPointInPolygon(centerLon, centerLat, ring)) return true;
        // Polygon boundary buffer ko touch karti hai
        return segmentInRadius(ring);
      }

      case "MultiPolygon":
        return geom.coordinates.some((polygon) => {
          const ring = polygon[0];
          if (isPointInPolygon(centerLon, centerLat, ring)) return true;
          return segmentInRadius(ring);
        });

      default:
        return true;
    }
  };
  // Clear all measurements
  const clearMeasurements = () => {
    if (measureLayerRef.current) {
      measureLayerRef.current.getSource().clear();
    }
    setMeasurements([]);
    stopMeasurement();
  };

  // Asset search

  const metersToDegrees = (meters, lat) => {
    const latDelta = meters / 111320;
    const lonDelta = meters / (111320 * Math.cos((lat * Math.PI) / 180));
    return { latDelta, lonDelta };
  };

  const getDistanceMeters = (lon1, lat1, lon2, lat2) => {
    const R = 6371000;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  const drawBuffer = (lon, lat, radiusMeters) => {
    if (!assetBufferLayerRef.current) return;
    const source = assetBufferLayerRef.current.getSource();
    source.clear();
    const points = 64;
    const coords = [];
    for (let i = 0; i < points; i++) {
      const angle = (i / points) * 2 * Math.PI;
      const { latDelta, lonDelta } = metersToDegrees(radiusMeters, lat);
      const pLon = lon + lonDelta * Math.cos(angle);
      const pLat = lat + latDelta * Math.sin(angle);
      coords.push(fromLonLat([pLon, pLat]));
    }
    coords.push(coords[0]);
    const circleFeature = new Feature({ geometry: new Polygon([coords]) });
    source.addFeature(circleFeature);
  };

  const performAssetSearch = async () => {
    if (!incidentPoint) {
      alert("Pehle map pe point select karo");
      return;
    }
    const radiusMeters =
      searchUnit === "kilometers" ? searchRange * 1000 : searchRange;
    drawBuffer(incidentPoint.lon, incidentPoint.lat, radiusMeters);
    setAssetSearchLoading(true);
    setAssetResults({});
    const { lon, lat } = incidentPoint;
    const { latDelta, lonDelta } = metersToDegrees(radiusMeters, lat);
    // Buffer se 2x bada bbox - large polygons bhi aa jayenge
    const bbox = `${lon - lonDelta * 2},${lat - latDelta * 2},${lon + lonDelta * 2},${lat + latDelta * 2},EPSG:4326`;
    const results = {};
    const layersToSearch = [];
    Object.entries(layerGroups).forEach(([groupKey, group]) => {
      Object.entries(group.children).forEach(([layerKey, layer]) => {
        if (!layer.children && layer.wmsLayer) {
          if (assetLayerFilter === "All" || assetLayerFilter === layerKey) {
            layersToSearch.push({ layerKey, layer });
          }
        }
      });
    });
    for (const { layerKey, layer } of layersToSearch) {
      try {
        const params = new URLSearchParams({
          service: "WFS",
          version: "1.0.0",
          request: "GetFeature",
          typeName: layer.wmsLayer,
          outputFormat: "application/json",
          srsName: "EPSG:4326",
          bbox,
        });
        const res = await fetch(`${GEOSERVER_CONFIG.wfsUrl}?${params}`);
        const data = await res.json();
        if (data.features && data.features.length > 0) {
          const filtered = data.features.filter((f) => {
            if (!f.geometry) return false;
            return isFeatureInRadius(f, lon, lat, radiusMeters);
          });
          if (filtered.length > 0) {
            results[layerKey] = { layerName: layer.name, features: filtered };
          }
        }
      } catch (e) {
        console.error(`Error fetching ${layerKey}:`, e);
      }
    }
    setAssetResults(results);
    const expanded = {};
    Object.keys(results).forEach((k) => (expanded[k] = false));
    setAssetResultsExpanded(expanded);
    setAssetSearchLoading(false);
  };

  const zoomToAssetFeature = (feature) => {
    if (!feature.geometry || !mapInstanceRef.current) return;
    const geojsonFormat = new GeoJSON();
    try {
      const olFeature = geojsonFormat.readFeature(feature, {
        dataProjection: "EPSG:4326",
        featureProjection: "EPSG:3857",
      });
      const geom = olFeature.getGeometry();
      const view = mapInstanceRef.current.getView();
      if (
        feature.geometry.type === "Point" ||
        feature.geometry.type === "MultiPoint"
      ) {
        const assetExtent = geom.getExtent();
        if (assetExtent && assetExtent.every((v) => isFinite(v))) {
          const [minX, minY, maxX, maxY] = assetExtent;
          if (maxX - minX < 1 && maxY - minY < 1) {
            view.animate({
              center: [(minX + maxX) / 2, (minY + maxY) / 2],
              zoom: 15,
              duration: 800,
            });
          } else {
            view.fit(assetExtent, {
              padding: [100, 100, 100, 100],
              duration: 800,
            });
          }
        }
      } else {
        view.fit(geom.getExtent(), {
          padding: [80, 80, 80, 80],
          duration: 800,
          maxZoom: 18,
        });
      }
      if (selectionLayerRef.current) {
        selectionLayerRef.current.getSource().clear();
        selectionLayerRef.current.getSource().addFeature(olFeature);
      }
    } catch (e) {
      console.error(e);
    }
  };
  const fetchFeaturesInBbox = async (extent4326) => {
    if (!queryLayer) return;
    const wmsLayerName = layerExtentsRef.current[queryLayer];
    if (!wmsLayerName) return;

    const bbox = `${extent4326[0]},${extent4326[1]},${extent4326[2]},${extent4326[3]},EPSG:4326`;
    try {
      const params = new URLSearchParams({
        ...DEFAULT_WFS_PARAMS,
        typeName: wmsLayerName,
        bbox,
      });
      const res = await fetch(`${GEOSERVER_CONFIG.wfsUrl}?${params}`);
      const data = await res.json();
      handleSelectResults(data, wmsLayerName);
    } catch (e) {
      console.error("BBox select error:", e);
    }
  };

  /**
   * Layer ka asli geometry column name nikalo (cached).
   * Ye layers "geometry" use karte hain, "the_geom" nahi — aur hardcoded
   * "the_geom" par GeoServer ServiceException deta hai, jiski wajah se polygon/
   * freehand selection chupchaap bbox fallback par chali jaati thi (yani aapke
   * drawn shape ke bajaye uske bounding box se selection hoti thi).
   */
  const getGeometryField = async (wmsLayerName) => {
    if (geomFieldCacheRef.current[wmsLayerName]) {
      return geomFieldCacheRef.current[wmsLayerName];
    }
    try {
      const params = new URLSearchParams({
        ...DEFAULT_WFS_PARAMS,
        typeName: wmsLayerName,
        maxFeatures: 1,
      });
      const res = await fetch(`${GEOSERVER_CONFIG.wfsUrl}?${params}`);
      const data = await res.json();
      const name = data.features?.[0]?.geometry_name || "geometry";
      geomFieldCacheRef.current[wmsLayerName] = name;
      return name;
    } catch (e) {
      return "geometry";
    }
  };

  const fetchFeaturesInGeometry = async (polygon4326) => {
    if (!queryLayer) return;
    const wmsLayerName = layerExtentsRef.current[queryLayer];
    if (!wmsLayerName) return;

    const coords = polygon4326
      .getCoordinates()[0]
      .map((c) => c.join(" "))
      .join(",");
    const geomField = await getGeometryField(wmsLayerName);
    const cqlFilter = `INTERSECTS(${geomField}, POLYGON((${coords})))`;

    try {
      const params = new URLSearchParams({
        ...DEFAULT_WFS_PARAMS,
        typeName: wmsLayerName,
        CQL_FILTER: cqlFilter,
      });
      const res = await fetch(`${GEOSERVER_CONFIG.wfsUrl}?${params}`);
      const data = await res.json();
      handleSelectResults(data, wmsLayerName);
    } catch (e) {
      // Fallback: bbox use karo
      const extent = polygon4326.getExtent();
      fetchFeaturesInBbox(extent);
    }
  };

  const handleSelectResults = async (data, wmsLayerName) => {
    if (!data.features || data.features.length === 0) {
      alert("No features selected");
      return;
    }

    const cols = Object.keys(data.features[0].properties).map((key) => ({
      id: key,
      label: key.toUpperCase().replace(/_/g, " "),
    }));
    setAttributeColumns(cols);

    // Puri layer ka data fetch karo
    try {
      const params = new URLSearchParams({
        ...DEFAULT_WFS_PARAMS,
        typeName: wmsLayerName,
      });
      const res = await fetch(`${GEOSERVER_CONFIG.wfsUrl}?${params}`);
      const allData = await res.json();

      if (allData.features?.length > 0) {
        const allRows = allData.features.map((f, i) => ({
          ...f.properties,
          __rowId: i,
        }));
        attributeFeaturesRef.current = allData.features;
        setAttributeData(allRows);
        setAttributeLayerName(wmsLayerName);

        // Selected features ko match karo — WFS ke UNIQUE feature id se.
        //
        // ⚠️ Pehle yahan `cols[0].id` (attribute table ka PEHLA column) ko
        // identity maan liya jaata tha. Kuch layers par wo OBJECTID hota hai
        // (isliye kabhi kabhi sahi lagta tha), lekin mdpe_pipeline/steel_pipeline
        // par pehla column "Dia" (diameter) hai — jo unique nahi hai. Us wajah se
        // 2-3 pipelines select karne par same diameter wale SAARE features
        // (Dia=63 par 940 rows!) select ho jaate the.
        // ⚠️ feature.id yahan use NAHI kar sakte: is GeoServer par fids har
        // request mein naye generate hote hain (do identical WFS calls same
        // features ke liye alag-alag fid dete hain). Isliye identity ke liye
        // geometry + properties ka combination use karo — wo stable hai.
        const featKey = (f) =>
          JSON.stringify([f.geometry, f.properties]);
        const selectedKeys = new Set(data.features.map(featKey));
        const selectedIds = allData.features
          .map((f, i) => (selectedKeys.has(featKey(f)) ? i : -1))
          .filter((i) => i >= 0);

        setSelectedRows(selectedIds);
        updateMapSelectionFromRows(selectedIds);
      }
    } catch (e) {
      console.error("Fetch all data error:", e);
    }

    setTablePage(0);
  };

  const clearAssetSearch = () => {
    setIncidentPoint(null);
    setAssetResults({});
    setAssetResultsExpanded({});
    setAssetSearchMode("idle");
    isAssetSelectingRef.current = false;
    if (assetBufferLayerRef.current)
      assetBufferLayerRef.current.getSource().clear();
    if (assetPointLayerRef.current)
      assetPointLayerRef.current.getSource().clear();
    if (selectionLayerRef.current) {
      selectionLayerRef.current.getSource().clear();
      selectionLayerRef.current.changed();
    }
  };
  // Asset searh end

  // ---------------- Direction tool ----------------
  // Map layer ko state se sync rakho (panel band ho tab bhi route dikhta rahe)
  useEffect(() => {
    const layer = directionLayerRef.current;
    if (!layer) return;
    const source = layer.getSource();
    source.clear();
    const feats = [];

    if (directionRoute?.geometry?.coordinates?.length) {
      const line = new Feature({
        geometry: new LineString(
          directionRoute.geometry.coordinates.map((c) => fromLonLat(c)),
        ),
      });
      line.set("kind", "route");
      feats.push(line);
    }

    const stepGeom =
      directionActiveStep &&
      directionRoute?.legs?.[directionActiveStep.leg]?.steps?.[
        directionActiveStep.step
      ]?.geometry;
    if (stepGeom?.coordinates?.length > 1) {
      const seg = new Feature({
        geometry: new LineString(stepGeom.coordinates.map((c) => fromLonLat(c))),
      });
      seg.set("kind", "step");
      feats.push(seg);
    }

    const placed = directionStops.filter((s) => s.lon != null && s.lat != null);
    placed.forEach((s, i) => {
      const f = new Feature({ geometry: new Point(fromLonLat([s.lon, s.lat])) });
      f.set("kind", "stop");
      f.set("index", i + 1);
      f.set("total", placed.length);
      feats.push(f);
    });

    source.addFeatures(feats);
  }, [directionStops, directionRoute, directionActiveStep]);

  // Pick mode mein cursor crosshair
  useEffect(() => {
    const el = mapRef.current;
    if (!el) return;
    el.style.cursor = directionPickingId != null ? "crosshair" : "";
    return () => {
      el.style.cursor = "";
    };
  }, [directionPickingId]);

  const fitDirectionRoute = useCallback((route) => {
    const r = route || directionRoute;
    const map = mapInstanceRef.current;
    if (!map || !r?.geometry?.coordinates?.length) return;
    const line = new LineString(r.geometry.coordinates.map((c) => fromLonLat(c)));
    map.getView().fit(line.getExtent(), {
      padding: [60, 60, 60, 60],
      duration: 600,
      maxZoom: 18,
    });
  }, [directionRoute]);

  const zoomToDirectionStep = useCallback(
    (leg, step) => {
      const map = mapInstanceRef.current;
      const s = directionRoute?.legs?.[leg]?.steps?.[step];
      if (!map || !s) return;
      setDirectionActiveStep({ leg, step });
      const coords = s.geometry?.coordinates;
      if (coords?.length > 1) {
        const line = new LineString(coords.map((c) => fromLonLat(c)));
        map.getView().fit(line.getExtent(), {
          padding: [80, 80, 80, 80],
          duration: 500,
          maxZoom: 18,
        });
      } else if (s.maneuver?.location) {
        map.getView().animate({
          center: fromLonLat(s.maneuver.location),
          zoom: Math.max(map.getView().getZoom(), 17),
          duration: 500,
        });
      }
    },
    [directionRoute],
  );

  const clearDirection = useCallback(() => {
    setDirectionStops(makeDirectionStops());
    setDirectionRoute(null);
    setDirectionError("");
    setDirectionLoading(false);
    setDirectionActiveStep(null);
    setDirectionPickingId(null);
    directionPickRef.current = null;
    directionLayerRef.current?.getSource().clear();
  }, []);
  // ---------------- Direction tool end ----------------

  useEffect(() => {
    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    } else {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    }
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizing]);

  const onCloseTool = useCallback(
    (toolId) => {
      const next = closeToolState(activeTools, toolId);
      setActiveTools(next.activeTools);
      setActiveTab(next.activeTab);
    },
    [activeTools],
  );

  const toolCtx = useMemo(
    () => ({
      drawType,
      setDrawType,
      drawnFeatures,
      setDrawnFeatures,
      mapInstanceRef,
      drawLayerRef,
      drawInteractionRef,
      isDrawingActiveRef,
      isDrawingActive,
      setIsDrawingActive,
      printTitle,
      setPrintTitle,
      printCreatedBy,
      printDpi,
      setPrintDpi,
      printGenerating,
      printProgress,
      printLogs,
      printError,
      handlePrint,
      measureType,
      setMeasureType,
      measureUnit,
      setMeasureUnit,
      measureAreaUnit,
      setMeasureAreaUnit,
      measurements,
      setMeasurements,
      isMeasuring,
      startMeasurement,
      stopMeasurement,
      clearMeasurements,
      measureLayerRef,
      bookmarkName,
      setBookmarkName,
      bookmarks,
      bookmarkPage,
      bookmarkPages,
      bookmarkTotal,
      bookmarkLoading,
      bookmarkError,
      loadBookmarks,
      handleSaveBookmark,
      handleGoToBookmark,
      handleDeleteBookmark,
      filterLayer,
      setFilterLayer,
      filterClauses,
      setFilterClauses,
      filterInvertWhere,
      setFilterInvertWhere,
      filterLayerFields,
      setFilterLayerFields,
      layerGroups,
      queryFieldValues,
      queryFieldValuesLoading,
      fetchFieldValues,
      activeFilters,
      setActiveFilters,
      wmsLayersRef,
      layerExtentsRef,
      layerNamesRef,
      applyFilterToLayer,
      setAttributeTableOpen,
      setAttributeLoading,
      setAttributeColumns,
      setAttributeData,
      setAttributeLayerName,
      attributeFeaturesRef,
      setAttributeTableDocked,
      setTablePage,
      GEOSERVER_CONFIG,
      DEFAULT_WFS_PARAMS,
      queryLayer,
      setQueryLayer,
      queryClauses,
      setQueryClauses,
      invertWhere,
      setInvertWhere,
      queryLayerFields,
      setQueryLayerFields,
      setQueryFieldValues,
      setQueryFieldValuesLoading,
      fetchLayerFields,
      handleApplyQuery,
      selectedQueryLayer,
      setSelectedQueryLayer,
      selectionLayerRef,
      selectedRowsRef,
      selectMode,
      setSelectMode,
      selectToolActive,
      selectedRows,
      setSelectedRows,
      tableViewMode,
      setTableViewMode,
      attributeData,
      attributeColumns,
      updateMapSelectionFromRows,
      selectInteractionRef,
      selectDrawLayerRef,
      fetchFeaturesInGeometry,
      fetchFeaturesInBbox,
      assetSearchMode,
      setAssetSearchMode,
      incidentPoint,
      searchRange,
      setSearchRange,
      searchUnit,
      setSearchUnit,
      assetLayerFilter,
      setAssetLayerFilter,
      assetResults,
      assetResultsExpanded,
      setAssetResultsExpanded,
      assetSearchLoading,
      isAssetSelectingRef,
      performAssetSearch,
      zoomToAssetFeature,
      clearAssetSearch,
      directionStops,
      setDirectionStops,
      directionRoute,
      setDirectionRoute,
      directionLoading,
      setDirectionLoading,
      directionError,
      setDirectionError,
      directionPickingId,
      setDirectionPickingId,
      directionPickRef,
      directionActiveStep,
      zoomToDirectionStep,
      fitDirectionRoute,
      clearDirection,
      onCloseTool,
    }),
    [
      drawType,
      drawnFeatures,
      isDrawingActive,
      printTitle,
      printCreatedBy,
      printDpi,
      printGenerating,
      printProgress,
      printLogs,
      printError,
      measureType,
      measureUnit,
      measureAreaUnit,
      measurements,
      isMeasuring,
      bookmarkName,
      bookmarks,
      bookmarkPage,
      bookmarkPages,
      bookmarkTotal,
      bookmarkLoading,
      bookmarkError,
      filterLayer,
      filterClauses,
      filterInvertWhere,
      filterLayerFields,
      layerGroups,
      queryFieldValues,
      queryFieldValuesLoading,
      activeFilters,
      queryLayer,
      queryClauses,
      invertWhere,
      queryLayerFields,
      selectedQueryLayer,
      selectMode,
      selectToolActive,
      selectedRows,
      tableViewMode,
      attributeData,
      attributeColumns,
      assetSearchMode,
      incidentPoint,
      searchRange,
      searchUnit,
      assetLayerFilter,
      assetResults,
      assetResultsExpanded,
      assetSearchLoading,
      directionStops,
      directionRoute,
      directionLoading,
      directionError,
      directionPickingId,
      directionActiveStep,
      zoomToDirectionStep,
      fitDirectionRoute,
      clearDirection,
      onCloseTool,
    ],
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        width: "100vw",
        bgcolor: "#f5f5f5",
        overflow: "hidden",
      }}
    >

      <AppHeader />
      {/* <AppBar
        position="static"
        sx={{
          bgcolor: "#003376",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          flexShrink: 0,
        }}
      >
        <Toolbar sx={{ minHeight: "60px !important", px: 2 }}>
          // {/* ADD BACK BUTTON - END */}
      {/* <Box
            sx={{ display: "flex", alignItems: "center", gap: 2, flexGrow: 1 }}
          >
            <Box
              component="img"
              src={`${process.env.PUBLIC_URL}/assets/HCG-logo-1.png`}
              alt="Mapgeoid City Gas Logo"
              sx={{
                width: "75px",
                objectFit: "contain",
                borderRadius: "8px",
                background: "white",
                padding: "3px",
                boxShadow: "0 4px 12px rgba(59, 130, 246, 0.4)",
              }}
            />
            <Typography
              variant="h5"
              sx={{ fontWeight: "bold", color: "#f6f9f6ff" }}
            >
              BlueView
            </Typography>
            <Divider
              orientation="vertical"
              flexItem
              sx={{ mx: 2, borderColor: "white" }}
            />
            <Typography variant="body2" sx={{ color: "#fefbfbff" }}>
              Haryana City Gas Distribution
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton
              size="small"
              sx={{ border: "1px solid #fcfcfcff", color: "white" }}
              onClick={(e) => setAnchorEl(e.currentTarget)}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => setAnchorEl(null)}
            >
              <MenuItem
                onClick={() => {
                  setAnchorEl(null);
                  onBackToHome();
                }}
              >
                Home
              </MenuItem>
              <MenuItem onClick={() => setAnchorEl(null)}>Profile</MenuItem>
              <MenuItem
                onClick={() => {
                  setAnchorEl(null);
                  onNavigateToDashboard();
                }}
              >
                Dashboard
              </MenuItem>
              <MenuItem onClick={() => setAnchorEl(null)}>Logout</MenuItem>
            </Menu>
          </Box>
        </Toolbar> 
      </AppBar> */}

      <Box
        sx={{
          display: "flex",
          flex: 1,
          position: "relative",
          overflow: "hidden",
          minHeight: 0,
        }}
      >
        <Drawer
          variant="persistent"
          anchor="left"
          open={drawerOpen}
          sx={{
            width: drawerOpen ? drawerWidth : 0, // CHANGE
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              width: drawerWidth, // CHANGE
              position: "relative",
              border: "none",
              borderRight: "1px solid #e0e0e0",
              height: "100%",
            },
          }}
        >
          <Box
            sx={{ display: "flex", flexDirection: "column", height: "100%" }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                borderBottom: "1px solid #e0e0e0",
                bgcolor: "#fafafa",
              }}
            >
              <Tabs
                value={activeTab}
                onChange={(e, newValue) => setActiveTab(newValue)}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{
                  flex: 1,
                  minHeight: "40px",
                  "& .MuiTab-root": {
                    minHeight: "40px",
                    textTransform: "none",
                    fontSize: "13px",
                    // MUI default minWidth 90px + 16px padding — 2 base tabs
                    // hi ~180px kha jaate the. Label ke hisaab se size do.
                    minWidth: 0,
                    px: 1.5,
                    whiteSpace: "nowrap",
                  },
                  "& .MuiTabScrollButton-root": {
                    width: 20,
                    color: "#1976d2",
                  },
                }}
              >
                <Tab label="Layers" />
                <Tab label="Legend" />
                {/* ⚠️ Tool tabs ko yahan INLINE rakhna zaroori hai.
                    MUI <Tabs> sirf apne DIRECT children ko clone karke unhe
                    value/selected/onChange deta hai. Pehle yahan
                    <ToolDrawerTabs /> (ek custom component) tha — MUI use ek
                    hi child samajhta tha aur wo props usi component ko milte
                    the, jo unhe aage <Tab> tak pass hi nahi karta tha. Isliye
                    tool ke tabs dikhte to the par unpar click karne se kuch
                    nahi hota tha (Legend/Layers par jaane ke baad tool tab
                    wapas nahi khulta tha). Array child React.Children.map se
                    flatten ho jaata hai, isliye har Tab ko apna index milta hai. */}
                {TOOL_ORDER.filter((id) => activeTools.includes(id)).map(
                  (id) => (
                    <Tab key={id} label={TOOL_LABELS[id]} />
                  ),
                )}
              </Tabs>
              <IconButton
                onClick={() => setDrawerOpen(!drawerOpen)}
                size="small"
                sx={{
                  mr: 1,
                  color: "#4caf50",
                  "&:hover": { bgcolor: "#f5f5f5" },
                }}
              >
                <FirstPageIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* <Box
              sx={{
                p: 1,
                borderBottom: "1px solid #e0e0e0",
                display: "flex",
                gap: 0.5,
              }}
            >
              <Button
                size="small"
                variant="outlined"
                startIcon={<UploadIcon />}
                sx={{ flex: 1, textTransform: "none", fontSize: "11px" }}
              >
                Upload
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<CloudDownloadIcon />}
                sx={{ flex: 1, textTransform: "none", fontSize: "11px" }}
              >
                Export
              </Button>
            </Box> */}

            {/* <Box sx={{ p: 1, borderBottom: "1px solid #e0e0e0" }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Filter layers..."
                value={layerFilter}
                onChange={(e) => setLayerFilter(e.target.value)}
                InputProps={{
                  endAdornment: layerFilter && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setLayerFilter("")}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box> */}

            <Box
              sx={{
                flex: 1,
                overflow: "auto",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {activeTab === 0 ? (
                // LAYERS TAB
                <List dense>
                  {Object.entries(layerGroups).map(([groupKey, group]) => (
                    <Box key={groupKey}>
                      <ListItemButton
                        onClick={() => toggleGroupExpanded(groupKey)}
                      >
                        <Checkbox
                          size="small"
                          checked={group.visible}
                          onChange={() => toggleGroupVisibility(groupKey)}
                          onClick={(e) => e.stopPropagation()}
                          sx={{ p: 0, mr: 1 }}
                        />
                        <LayersIcon
                          sx={{ mr: 1, fontSize: 20, color: "#1976d2" }}
                        />
                        <ListItemText
                          primary={group.name}
                          primaryTypographyProps={{
                            fontSize: "14px",
                            fontWeight: 500,
                          }}
                        />
                        {group.expanded ? <ExpandLess /> : <ExpandMore />}
                      </ListItemButton>

                      <Collapse
                        in={group.expanded}
                        timeout="auto"
                        unmountOnExit
                      >
                        <List component="div" disablePadding>
                          {Object.entries(group.children).map(
                            ([layerKey, layer]) => (
                              <Box key={layerKey}>
                                {layer.children ? (
                                  <>
                                    <ListItemButton
                                      onClick={() =>
                                        toggleNestedExpanded(groupKey, layerKey)
                                      }
                                      sx={{ pl: 4 }}
                                    >
                                      <Checkbox
                                        size="small"
                                        checked={layer.visible}
                                        onChange={() =>
                                          toggleLayerVisibility(
                                            groupKey,
                                            layerKey,
                                          )
                                        }
                                        onClick={(e) => e.stopPropagation()}
                                        sx={{ p: 0, mr: 1 }}
                                      />
                                      <ListItemText
                                        primary={layer.name}
                                        primaryTypographyProps={{
                                          fontSize: "13px",
                                          fontWeight: 500,
                                        }}
                                      />
                                      {layer.expanded ? (
                                        <ExpandLess />
                                      ) : (
                                        <ExpandMore />
                                      )}
                                    </ListItemButton>

                                    <Collapse
                                      in={layer.expanded}
                                      timeout="auto"
                                      unmountOnExit
                                    >
                                      <List component="div" disablePadding>
                                        {Object.entries(layer.children).map(
                                          ([nestedKey, nestedLayer]) => (
                                            <ListItem
                                              key={nestedKey}
                                              sx={{
                                                pl: 8,
                                                display: "flex",
                                                alignItems: "center",
                                              }}
                                            >
                                              <Checkbox
                                                size="small"
                                                checked={nestedLayer.visible}
                                                onChange={() =>
                                                  toggleLayerVisibility(
                                                    groupKey,
                                                    layerKey,
                                                    nestedKey,
                                                  )
                                                }
                                                sx={{ p: 0, mr: 1 }}
                                              />
                                              <ListItemText
                                                primary={nestedLayer.name}
                                                secondary={
                                                  nestedLayer.visible
                                                    ? "Visible"
                                                    : "Hidden"
                                                }
                                                primaryTypographyProps={{
                                                  fontSize: "12px",
                                                }}
                                                secondaryTypographyProps={{
                                                  fontSize: "10px",
                                                }}
                                              />
                                              <IconButton
                                                size="small"
                                                onClick={(e) =>
                                                  handleLayerMenuClick(
                                                    e,
                                                    groupKey,
                                                    layerKey,
                                                    nestedKey,
                                                  )
                                                }
                                                sx={{
                                                  ml: "auto",
                                                  color: "rgba(0,0,0,0.54)",
                                                  "&:hover": {
                                                    bgcolor: "#e3f2fd",
                                                    color: "#0d47a1",
                                                  },
                                                }}
                                              >
                                                <MoreVertIcon fontSize="small" />
                                              </IconButton>
                                            </ListItem>
                                          ),
                                        )}
                                      </List>
                                    </Collapse>
                                  </>
                                ) : (
                                  <ListItem
                                    sx={{
                                      pl: 6,
                                      display: "flex",
                                      alignItems: "center",
                                      bgcolor:
                                        selectedQueryLayer === layerKey
                                          ? "rgba(25, 118, 210, 0.12)"
                                          : "transparent",
                                      borderLeft:
                                        selectedQueryLayer === layerKey
                                          ? "3px solid #1976d2"
                                          : "3px solid transparent",
                                      transition: "all 0.2s",
                                    }}
                                  >
                                    <Checkbox
                                      size="small"
                                      checked={layer.visible}
                                      onChange={() =>
                                        toggleLayerVisibility(
                                          groupKey,
                                          layerKey,
                                        )
                                      }
                                      sx={{ p: 0, mr: 1 }}
                                    />
                                    <ListItemText
                                      primary={layer.name}
                                      primaryTypographyProps={{
                                        fontSize: "13px",
                                      }}
                                      secondaryTypographyProps={{
                                        fontSize: "11px",
                                      }}
                                    />
                                    <IconButton
                                      size="small"
                                      onClick={(e) =>
                                        handleLayerMenuClick(
                                          e,
                                          groupKey,
                                          layerKey,
                                        )
                                      }
                                      sx={{
                                        ml: "auto",
                                        "&:hover": {
                                          bgcolor: "#e3f2fd",
                                          color: "#1976d2",
                                        },
                                      }}
                                    >
                                      <MoreVertIcon fontSize="small" />
                                    </IconButton>
                                  </ListItem>
                                )}
                              </Box>
                            ),
                          )}
                        </List>
                      </Collapse>

                      <Divider />
                    </Box>
                  ))}
                </List>
              ) : activeTab === 1 ? (
                // LEGEND TAB
                <Box sx={{ p: 2 }}>
                  <Typography
                    variant="h6"
                    sx={{ mb: 2, fontWeight: 600, color: "#1976d2" }}
                  >
                    Map Legend
                  </Typography>

                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    {Object.entries(layerGroups).map(([groupKey, group]) => {
                      return Object.entries(group.children).map(
                        ([layerKey, layer]) => {
                          if (!layer.visible) return null;

                          if (layer.children) {
                            return Object.entries(layer.children).map(
                              ([nestedKey, nestedLayer]) => {
                                if (!nestedLayer.visible) return null;

                                return (
                                  <Box key={nestedKey}>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontSize: "12px",
                                        fontWeight: 500,
                                        mb: 0.5,
                                      }}
                                    >
                                      {nestedLayer.name}
                                    </Typography>
                                    <img
                                      src={`${nestedLayer.wmsUrl || GEOSERVER_CONFIG.wmsUrl}?REQUEST=GetLegendGraphic&VERSION=1.0.0&FORMAT=image/png&LAYER=${nestedLayer.wmsLayer}&scale=50`}
                                      alt={nestedLayer.name}
                                      style={{
                                        display: "block",
                                        maxWidth: "100px",
                                      }}
                                      onError={(e) => {
                                        e.target.style.display = "none";
                                      }}
                                    />
                                  </Box>
                                );
                              },
                            );
                          } else {
                            return (
                              <Box key={layerKey}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontSize: "13px",
                                    fontWeight: 500,
                                    mb: 0.5,
                                  }}
                                >
                                  {layer.name}
                                </Typography>
                                <img
                                  src={`${layer.wmsUrl || GEOSERVER_CONFIG.wmsUrl}?REQUEST=GetLegendGraphic&VERSION=1.0.0&FORMAT=image/png&LAYER=${layer.wmsLayer}&scale=50`}
                                  alt={layer.name}
                                  style={{
                                    display: "block",
                                    maxWidth: "100px",
                                  }}
                                  onError={(e) => {
                                    e.target.style.display = "none";
                                  }}
                                />
                              </Box>
                            );
                          }
                        },
                      );
                    })}
                  </Box>

                  {Object.entries(layerGroups).every(([groupKey, group]) =>
                    Object.entries(group.children).every(
                      ([layerKey, layer]) => !layer.visible,
                    ),
                  ) && (
                      <Box sx={{ textAlign: "center", py: 4, color: "#999" }}>
                        <LayersIcon sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
                        <Typography variant="body2">
                          No visible layers to show in legend
                        </Typography>
                      </Box>
                    )}
                </Box>
              ) : (
                <ToolDrawerPanels
                  activeTab={activeTab}
                  activeTools={activeTools}
                  ctx={toolCtx}
                />
              )}
            </Box>
          </Box>
          {/* Resize Handle - ADD THIS */}
          {drawerOpen && (
            <Box
              onMouseDown={handleMouseDown}
              sx={{
                position: "absolute",
                right: 0,
                top: 0,
                bottom: 0,
                width: 4,
                cursor: "col-resize",
                bgcolor: "transparent",
                "&:hover": {
                  bgcolor: "#1976d2",
                },
                zIndex: 1001,
              }}
            />
          )}
          {/* ADD THIS MENU BELOW */}
          <Menu
            anchorEl={layerMenuAnchor}
            open={Boolean(layerMenuAnchor)}
            onClose={handleLayerMenuClose}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            PaperProps={{
              sx: {
                border: "2px solid #1976d2",
                boxShadow: "0 4px 12px rgba(25, 118, 210, 0.2)",
                borderRadius: 1,
              },
            }}
          >
            <MenuItem
              onClick={handleZoomToLayer}
              sx={{
                "&:hover": {
                  bgcolor: "#e3f2fd",
                  color: "#1976d2",
                },
              }}
            >
              <SvgIcon sx={{ mr: 1, fontSize: 20 }}>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M2 5l5-2.5 5 2.5 5-2.5v11l-5 2.5-5-2.5-5 2.5V5z"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinejoin="round"
                    fill="white"
                  />
                  <circle
                    cx="15.5"
                    cy="15.5"
                    r="5"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    fill="white"
                  />
                  <line
                    x1="19.5"
                    y1="19.5"
                    x2="30"
                    y2="30"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                  />
                  <line
                    x1="15.5"
                    y1="13"
                    x2="15.5"
                    y2="18"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                  />
                  <line
                    x1="13"
                    y1="15.5"
                    x2="18"
                    y2="15.5"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                  />
                </svg>
              </SvgIcon>
              Zoom to Layer
            </MenuItem>
            <MenuItem
              onClick={handleOpenAttributeTable}
              sx={{
                "&:hover": {
                  bgcolor: "#e3f2fd",
                  color: "#1976d2",
                },
              }}
            >
              <TableViewIcon sx={{ fontSize: 20, mr: 0.8 }} />
              Attribute Table
            </MenuItem>
          </Menu>
        </Drawer>

        <Box
          sx={{
            flex: 1,
            position: "relative",
            minHeight: 0,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {toolsOpen && (
            <ToolsFloatingBar
              activeTools={activeTools}
              setActiveTools={setActiveTools}
              setActiveTab={setActiveTab}
              setDrawerOpen={setDrawerOpen}
            />
          )}

          <Box
            sx={{
              flex: 1,
              position: "relative",
              minHeight: 0,
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: 8,
                left: 8,
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                zIndex: 1000,
              }}
            >
              {!drawerOpen && (
                <IconButton
                  onClick={() => setDrawerOpen(true)}
                  size="small"
                  sx={{
                    border: "2px solid #1976d2",
                    borderRadius: "4px",
                    bgcolor: "white",
                    color: "#1976d2",
                    width: 32,
                    height: 32,
                    boxShadow: 2,
                    "&:hover": {
                      bgcolor: "#4caf50",
                      color: "white",
                    },
                  }}
                >
                  <LastPageIcon fontSize="small" />
                </IconButton>
              )}
              <IconButton
                onClick={handleHome}
                size="small"
                sx={{
                  border: "2px solid #1976d2",
                  borderRadius: "4px",
                  bgcolor: "white",
                  color: "#1976d2",
                  width: 32,
                  height: 32,
                  boxShadow: 2,
                  "&:hover": {
                    bgcolor: "#4caf50",
                    color: "white",
                  },
                }}
              >
                <HomeIcon fontSize="small" />
              </IconButton>
              <IconButton
                onClick={handleZoomIn}
                size="small"
                sx={{
                  border: "2px solid #1976d2",
                  borderRadius: "4px",
                  bgcolor: "white",
                  color: "#1976d2",
                  width: 32,
                  height: 32,
                  boxShadow: 2,
                  "&:hover": {
                    bgcolor: "#4caf50",
                    color: "white",
                  },
                }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
              <IconButton
                onClick={handleZoomOut}
                size="small"
                sx={{
                  border: "2px solid #1976d2",
                  borderRadius: "4px",
                  bgcolor: "white",
                  color: "#1976d2",
                  width: 32,
                  height: 32,
                  boxShadow: 2,
                  "&:hover": {
                    bgcolor: "#4caf50",
                    color: "white",
                  },
                }}
              >
                <RemoveIcon fontSize="small" />
              </IconButton>
            </Box>

            <Box
              ref={mapRef}
              sx={{
                width: "100%",
                height: "100%",
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                bgcolor: "#f0f0f0",
              }}
            />
            {/* Feature Info Popup */}
            {/* Feature Info Popup */}
            {/* Feature Info Popup */}
            {popupOpen && (
              <Rnd
                position={popupPosition}
                onDragStop={(e, d) => setPopupPosition({ x: d.x, y: d.y })}
                enableResizing={false}
                dragHandleClassName="popup-drag-handle"
                style={{ zIndex: 9999, position: "fixed" }}
              >
                <Box
                  id="popup-container"
                  sx={{
                    bgcolor: "white",
                    border: "2px solid #1976d2",
                    borderRadius: 1,
                    minWidth: 280,
                    maxWidth: 360,
                    boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
                    overflow: "hidden",
                    pointerEvents: "auto",
                  }}
                >
                  {/* Header */}
                  <Box
                    className="popup-drag-handle"
                    sx={{
                      bgcolor: "#1976d2",
                      color: "white",
                      py: 0.5,
                      px: 1.5,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      cursor: "move",
                      userSelect: "none",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ fontSize: 13, fontWeight: 600 }}
                    >
                      Feature Information
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => {
                        isPopupClosingRef.current = true;
                        setPopupOpen(false);
                        setAllPopupFeatures([]);
                        if (selectionLayerRef.current) {
                          selectionLayerRef.current.getSource().clear();
                          selectionLayerRef.current.changed();
                        }
                      }}
                      sx={{ color: "white", p: 0.3 }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  {/* Content */}
                  <Box sx={{ maxHeight: 290, overflow: "auto", p: 1 }}>
                    {allPopupFeatures.map((featureData, featureIndex) => {
                      const isFeatureExpanded =
                        expandedAttributes[`feature-${featureIndex}`];
                      return (
                        <Paper
                          key={featureIndex}
                          elevation={2}
                          sx={{
                            mb: 1.5,
                            border: "1px solid #e0e0e0",
                            borderRadius: 1,
                            overflow: "hidden",
                          }}
                        >
                          <Box
                            onClick={() =>
                              setExpandedAttributes((prev) => ({
                                ...prev,
                                [`feature-${featureIndex}`]:
                                  !prev[`feature-${featureIndex}`],
                              }))
                            }
                            sx={{
                              bgcolor: "#e3f2fd",
                              px: 1.5,
                              py: 0.8,
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              borderBottom: "1px solid #1976d2",
                              cursor: "pointer",
                              "&:hover": { bgcolor: "#bbdefb" },
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <LayersIcon
                                sx={{ fontSize: 16, color: "#1976d2" }}
                              />
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  fontSize: 12,
                                  color: "#1976d2",
                                }}
                              >
                                {featureData.layerName}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleZoomToFeature(featureData);
                                }}
                                title="Zoom to feature"
                                sx={{
                                  bgcolor: "white",
                                  border: "1px solid #1976d2",
                                  p: 0.3,
                                  "&:hover": {
                                    bgcolor: "#1976d2",
                                    color: "white",
                                  },
                                }}
                              >
                                <SearchIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                              {isFeatureExpanded ? (
                                <ExpandLess
                                  sx={{ fontSize: 18, color: "#1976d2" }}
                                />
                              ) : (
                                <ExpandMore
                                  sx={{ fontSize: 18, color: "#1976d2" }}
                                />
                              )}
                            </Box>
                          </Box>
                          <Collapse in={isFeatureExpanded} timeout="auto">
                            <Box sx={{ p: 1.5, bgcolor: "#fafafa" }}>
                              {Object.entries(featureData.properties).map(
                                ([key, value], attrIndex) => (
                                  <Box
                                    key={attrIndex}
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      mb: 0.8,
                                      pb: 0.8,
                                      borderBottom:
                                        attrIndex <
                                          Object.entries(featureData.properties)
                                            .length -
                                          1
                                          ? "1px solid #e0e0e0"
                                          : "none",
                                      gap: 2,
                                    }}
                                  >
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontWeight: 600,
                                        color: "#555",
                                        fontSize: 11,
                                        textTransform: "capitalize",
                                        minWidth: "100px",
                                        flexShrink: 0,
                                      }}
                                    >
                                      {key.replace(/_/g, " ")}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontSize: 11,
                                        color: "#333",
                                        wordBreak: "break-word",
                                        textAlign: "right",
                                        flex: 1,
                                      }}
                                    >
                                      {value !== null && value !== undefined
                                        ? String(value)
                                        : "-"}
                                    </Typography>
                                  </Box>
                                ),
                              )}
                            </Box>
                          </Collapse>
                        </Paper>
                      );
                    })}
                  </Box>
                </Box>
              </Rnd>
            )}

            {/* Tools Button */}
            <Paper
              sx={{
                position: "absolute",
                top: 16,
                right: 16,
                zIndex: 1002,
                borderRadius: 1,
              }}
            >
              <Button
                variant="contained"
                startIcon={<BuildIcon sx={{ fontSize: 16 }} />}
                onClick={() => setToolsOpen(!toolsOpen)}
                sx={{
                  textTransform: "none",
                  bgcolor: "white",
                  color: "#1976d2",
                  border: "2px solid #1976d2",
                  fontWeight: 500,
                  fontSize: "12px",
                  padding: "6px 12px",
                  height: "36px",
                  "&:hover": {
                    bgcolor: "#4caf50",
                    color: "white",
                    borderColor: "#4caf50",
                  },
                }}
              >
                {toolsOpen ? "Hide" : "Tools"}
              </Button>
            </Paper>

            {/* Search Bar */}
            <Box
              sx={{
                position: "absolute",
                top: 16,
                right: 103,
                zIndex: 1000,
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              {searchOpen && (
                <Box sx={{ position: "relative" }}>
                  <Paper sx={{ display: "flex", alignItems: "center" }}>
                    <TextField
                      placeholder="Search location..."
                      size="small"
                      value={searchQuery}
                      onChange={(e) => handleSearchInput(e.target.value)}
                      onKeyPress={handleSearchKeyPress}
                      sx={{
                        width: 210,
                        bgcolor: "white",
                        "& .MuiInputBase-root": {
                          height: 36,
                          fontSize: "13px",
                        },
                      }}
                    />
                    <IconButton
                      onClick={handleSearch}
                      size="small"
                      sx={{
                        bgcolor: "#1976d2",
                        color: "white",
                        width: 32,
                        height: 32,
                        mr: 0.5,
                        "&:hover": {
                          bgcolor: "#4caf50",
                        },
                      }}
                    >
                      <SearchIcon fontSize="small" />
                    </IconButton>
                  </Paper>

                  {showSuggestions && searchResults.length > 0 && (
                    <Paper
                      sx={{
                        position: "absolute",
                        top: 40,
                        left: 0,
                        width: 250,
                        maxHeight: 300,
                        overflow: "auto",
                        zIndex: 1001,
                      }}
                    >
                      <List dense>
                        {searchResults.map((result, index) => (
                          <ListItemButton
                            key={index}
                            onClick={() => goToLocation(result)}
                            sx={{
                              "&:hover": {
                                bgcolor: "#e3f2fd",
                              },
                            }}
                          >
                            <LocationOnIcon
                              sx={{
                                mr: 1,
                                fontSize: 18,
                                color: "#1976d2",
                              }}
                            />
                            <ListItemText
                              primary={result.display_name}
                              primaryTypographyProps={{
                                fontSize: "12px",
                                noWrap: false,
                              }}
                            />
                          </ListItemButton>
                        ))}
                      </List>
                    </Paper>
                  )}
                </Box>
              )}
              <IconButton
                onClick={() => {
                  setSearchOpen(!searchOpen);
                  if (searchOpen) {
                    setSearchQuery("");
                    setSearchResults([]);
                    setShowSuggestions(false);
                    // Clear marker when closing search
                    if (markerLayerRef.current) {
                      const vectorSource = markerLayerRef.current.getSource();
                      vectorSource.clear();
                    }
                  }
                }}
                size="small"
                sx={{
                  bgcolor: "white",
                  border: "2px solid #1976d2",
                  borderRadius: "4px",
                  color: "#1976d2",
                  width: 36,
                  height: 36,
                  boxShadow: 2,
                  "&:hover": {
                    bgcolor: "#4caf50",
                    color: "white",
                    borderColor: "#4caf50",
                  },
                }}
              >
                {searchOpen ? (
                  <CloseIcon fontSize="small" />
                ) : (
                  <SearchIcon fontSize="small" />
                )}
              </IconButton>
            </Box>

            <Box
              sx={{
                position: "absolute",
                bottom: 60,
                right: 16,
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                zIndex: 1000,
              }}
            >
              <IconButton
                onClick={handleMyLocation}
                size="small"
                sx={{
                  bgcolor: "white",
                  border: "2px solid #1976d2",
                  borderRadius: "4px",
                  color: "#1976d2",
                  width: 32,
                  height: 32,
                  boxShadow: 2,
                  "&:hover": {
                    bgcolor: "#4caf50",
                    color: "white",
                  },
                }}
              >
                <MyLocationIcon fontSize="small" />
              </IconButton>
              <IconButton
                onClick={(e) => setBaseMapMenuAnchor(e.currentTarget)}
                size="small"
                sx={{
                  bgcolor: "white",
                  border: "2px solid #1976d2",
                  borderRadius: "4px",
                  color: "#1976d2",
                  width: 32,
                  height: 32,
                  boxShadow: 2,
                  "&:hover": {
                    bgcolor: "#4caf50",
                    color: "white",
                  },
                }}
              >
                <PublicIcon fontSize="small" />
              </IconButton>

              <Menu
                anchorEl={baseMapMenuAnchor}
                open={Boolean(baseMapMenuAnchor)}
                onClose={() => setBaseMapMenuAnchor(null)}
                anchorOrigin={{ vertical: "top", horizontal: "left" }}
                transformOrigin={{ vertical: "bottom", horizontal: "right" }}
                PaperProps={{
                  sx: { width: 280 },
                }}
              >
                {Object.keys(baseMaps).map((mapType) => (
                  <MenuItem
                    key={mapType}
                    onClick={() => changeBaseMap(mapType)}
                    selected={currentBaseMap === mapType}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      py: 1,
                    }}
                  >
                    {/* Real Thumbnail Image */}
                    <Box
                      sx={{
                        width: 50,
                        height: 50,
                        border:
                          currentBaseMap === mapType
                            ? "2px solid #1976d2"
                            : "1px solid #e0e0e0",
                        borderRadius: 1,
                        overflow: "hidden",
                        flexShrink: 0,
                      }}
                    >
                      <img
                        src={baseMaps[mapType].thumbnail}
                        alt={baseMaps[mapType].name}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    </Box>

                    <ListItemText>{baseMaps[mapType].name}</ListItemText>

                    {currentBaseMap === mapType && (
                      <CheckCircleIcon fontSize="small" color="primary" />
                    )}
                  </MenuItem>
                ))}
              </Menu>
            </Box>

            <Paper
              square
              sx={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                p: 0.5,
                px: 1.5,
                display: "flex",
                justifyContent: "space-between",
                zIndex: 1000,
              }}
            >
              <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                <Select
                  size="small"
                  value={coordFormat}
                  onChange={(e) => setCoordFormat(e.target.value)}
                  sx={{ fontSize: "11px" }}
                >
                  <MenuItem value="xy">X/Y</MenuItem>
                  <MenuItem value="latlon">Lat/Lon</MenuItem>
                </Select>
                <Box ref={coordinatesDivRef} sx={{ display: "flex", gap: 1.5 }}>
                  <Typography
                    id="coord-x"
                    variant="caption"
                    sx={{ fontSize: "10px" }}
                  >
                    X: 0
                  </Typography>
                  <Typography
                    id="coord-y"
                    variant="caption"
                    sx={{ fontSize: "11px" }}
                  >
                    Y: 0
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                <Typography variant="caption">Scale</Typography>
                <TextField
                  size="small"
                  value={scaleInput}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    // Only allow numbers
                    if (/^\d*$/.test(newValue)) {
                      setScaleInput(newValue);
                    }
                  }}
                  onKeyPress={(e) => {
                    if (e.key === "Enter" && scaleInput) {
                      const newScale = parseInt(scaleInput);
                      const view = mapInstanceRef.current?.getView();
                      if (view && !isNaN(newScale) && newScale > 0) {
                        const dpi = 25.4 / 0.28;
                        const units = view.getProjection().getUnits();
                        const mpu =
                          units === "degrees" ? 111194.87428468118 : 1;
                        const resolution = newScale / (mpu * 39.37 * dpi);
                        view.setResolution(resolution);
                      }
                    }
                  }}
                  onBlur={() => {
                    // Reset to actual scale if user didn't press Enter
                    setScaleInput(scale);
                  }}
                  InputProps={{
                    startAdornment: (
                      <Typography
                        variant="caption"
                        sx={{ mr: 0.5, color: "#666" }}
                      >
                        1:
                      </Typography>
                    ),
                  }}
                  sx={{
                    fontSize: "12px",
                    width: 100,
                    "& .MuiInputBase-root": {
                      height: 26,
                      fontSize: "12px",
                    },
                    "& input": {
                      fontSize: "12px",
                      padding: "3px 3px 3px 0",
                    },
                  }}
                  placeholder="11541"
                />
              </Box>
            </Paper>
            {/* Bookmark Dialog */}


            {/* Attribute Table - Draggable, Resizable, Dockable */}
            {attributeTableOpen && !attributeTableDocked && (
              <Rnd
                size={attributeTableSize}
                position={attributeTablePosition}
                onDragStop={(e, d) => {
                  setAttributeTablePosition({ x: d.x, y: d.y });
                }}
                onResizeStop={(e, direction, ref, delta, position) => {
                  setAttributeTableSize({
                    width: ref.offsetWidth,
                    height: ref.offsetHeight,
                  });
                  setAttributeTablePosition(position);
                }}
                minWidth={400}
                minHeight={300}
                enableResizing={{
                  top: true,
                  right: true,
                  bottom: true,
                  left: true,
                  topRight: true,
                  bottomRight: true,
                  bottomLeft: true,
                  topLeft: true,
                }}
                dragHandleClassName="drag-handle"
                style={{
                  zIndex: 1200,
                  position: "fixed",
                }}
              >
                <Paper
                  elevation={8}
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    border: "2px solid #1976d2",
                    borderRadius: 1,
                    overflow: "hidden",
                  }}
                >
                  {/* Header - Draggable Handle */}
                  <Box
                    className="drag-handle"
                    sx={{
                      bgcolor: "#1976d2",
                      color: "white",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      py: 0.1,
                      px: 0.3,
                      cursor: "move",
                      userSelect: "none",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <TableChartOutlinedIcon sx={{ fontSize: 18 }} />
                      <Typography variant="h6" sx={{ fontSize: 16 }}>
                        {attributeLayerName}
                      </Typography>
                    </Box>
                    <Box
                      sx={{ display: "flex", gap: 0.5, alignItems: "center" }}
                    >
                      {/* ✅ NAYA - Zoom to Selected Button */}
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={handleZoomToSelected}
                        disabled={selectedRows.length === 0}
                        startIcon={<MapIcon sx={{ fontSize: 14 }} />}
                        sx={{
                          color: "white",
                          borderColor: "rgba(255,255,255,0.5)",
                          fontSize: 11,
                          py: 0.2,
                          px: 1,
                          "&:hover": {
                            bgcolor: "rgba(255,255,255,0.15)",
                            borderColor: "white",
                          },
                          "&.Mui-disabled": {
                            color: "rgba(255,255,255,0.4)",
                            borderColor: "rgba(255,255,255,0.2)",
                          },
                        }}
                      >
                        Zoom to Selected
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          setSelectedRows([]);
                          if (selectionLayerRef.current) {
                            selectionLayerRef.current.getSource().clear();
                            selectionLayerRef.current.changed();
                          }
                        }}
                        disabled={selectedRows.length === 0}
                        sx={{
                          color: "white",
                          borderColor: "rgba(255,100,100,0.7)",
                          fontSize: 11,
                          py: 0.2,
                          px: 1,
                          mr: 1,
                          "&:hover": {
                            bgcolor: "rgba(255,100,100,0.2)",
                            borderColor: "white",
                          },
                          "&.Mui-disabled": {
                            color: "rgba(255,255,255,0.4)",
                            borderColor: "rgba(255,255,255,0.2)",
                          },
                        }}
                      >
                        Clear
                      </Button>
                      {/* Dock Button */}
                      <IconButton
                        onClick={() => setAttributeTableDocked(true)}
                        sx={{ color: "white" }}
                        size="small"
                        title="Dock to bottom"
                      >
                        <DockIcon />
                      </IconButton>
                      {/* Close Button */}
                      <IconButton
                        onClick={() => setAttributeTableOpen(false)}
                        sx={{ color: "white" }}
                        size="small"
                      >
                        <CloseIcon />
                      </IconButton>
                    </Box>
                  </Box>

                  {/* Content */}
                  <Box sx={{ flex: 1, overflow: "hidden", bgcolor: "#f5f5f5" }}>
                    {attributeLoading ? (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          height: "100%",
                          flexDirection: "column",
                          gap: 2,
                        }}
                      >
                        <CircularProgress size={60} />
                        <Typography variant="body1" color="text.secondary">
                          Loading attribute data...
                        </Typography>
                      </Box>
                    ) : attributeData.length === 0 ? (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          height: "100%",
                          flexDirection: "column",
                          gap: 2,
                          color: "#999",
                        }}
                      >
                        <LayersIcon sx={{ fontSize: 64, opacity: 0.3 }} />
                        <Typography variant="h6">No data available</Typography>
                      </Box>
                    ) : (
                      <VirtualTable
                        attributeData={attributeData}
                        attributeColumns={attributeColumns}
                        selectedRows={selectedRows}
                        setSelectedRows={setSelectedRows}
                        updateMapSelectionFromRows={updateMapSelectionFromRows}
                        tableViewMode={tableViewMode}
                      />
                    )}
                  </Box>

                  {/* Footer */}
                  <Box
                    sx={{
                      bgcolor: "#f5f5f5",
                      borderTop: "1px solid #e0e0e0",
                      px: 1,
                      py: 0.5,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        border: "1px solid #1976d2",
                        borderRadius: 1,
                        overflow: "hidden",
                      }}
                    >
                      <Button
                        size="small"
                        onClick={() => setTableViewMode("all")}
                        sx={{
                          borderRadius: 0,
                          px: 1.5,
                          py: 0.3,
                          fontSize: 11,
                          minWidth: 0,
                          bgcolor:
                            tableViewMode === "all" ? "#1976d2" : "white",
                          color: tableViewMode === "all" ? "white" : "#1976d2",
                          "&:hover": {
                            bgcolor:
                              tableViewMode === "all" ? "#115293" : "#e3f2fd",
                          },
                        }}
                      >
                        All ({attributeData.length})
                      </Button>
                      <Box sx={{ width: "1px", bgcolor: "#1976d2" }} />
                      <Button
                        size="small"
                        onClick={() => setTableViewMode("selected")}
                        sx={{
                          borderRadius: 0,
                          px: 1.5,
                          py: 0.3,
                          fontSize: 11,
                          minWidth: 0,
                          bgcolor:
                            tableViewMode === "selected" ? "#1976d2" : "white",
                          color:
                            tableViewMode === "selected" ? "white" : "#1976d2",
                          "&:hover": {
                            bgcolor:
                              tableViewMode === "selected"
                                ? "#115293"
                                : "#e3f2fd",
                          },
                        }}
                      >
                        Selected ({selectedRows.length})
                      </Button>
                    </Box>

                    <Button
                      onClick={() => {
                        const exportData =
                          tableViewMode === "selected"
                            ? attributeData.filter((r) =>
                              selectedRows.includes(r.id),
                            )
                            : attributeData;
                        const csv = [
                          attributeColumns.map((col) => col.label).join(","),
                          ...exportData.map((row) =>
                            attributeColumns
                              .map((col) => row[col.id])
                              .join(","),
                          ),
                        ].join("\n");
                        const blob = new Blob([csv], { type: "text/csv" });
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = `${attributeLayerName}_data.csv`;
                        a.click();
                      }}
                      variant="outlined"
                      size="small"
                      startIcon={<CloudDownloadIcon />}
                    >
                      Export CSV
                    </Button>
                  </Box>
                </Paper>
              </Rnd>
            )}

            {/* Docked Attribute Table */}
            {attributeTableOpen && attributeTableDocked && (
              <Paper
                elevation={8}
                sx={{
                  position: "fixed",
                  bottom: 55,
                  left: drawerOpen ? drawerWidth + 5 : 20,
                  right: 53,
                  height: `${dockedTableHeight}px`,
                  zIndex: 1200,
                  display: "flex",
                  flexDirection: "column",
                  border: "2px solid #1976d2",
                  borderBottom: "none",
                  borderRadius: "8px 8px 0 0",
                  overflow: "hidden",
                  transition: "left 0.3s ease",
                }}
              >
                {/* Resize Handle - Top edge */}
                <Box
                  onMouseDown={handleDockedResizeStart}
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 6,
                    cursor: "ns-resize",
                    bgcolor: "transparent",
                    zIndex: 1300,
                    "&:hover": {
                      bgcolor: "#1976d2",
                    },
                  }}
                />

                {/* Header */}
                <Box
                  sx={{
                    bgcolor: "#1976d2",
                    color: "white",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    py: 0.1,
                    px: 1,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <TableChartOutlinedIcon sx={{ fontSize: 18 }} />
                    <Typography variant="h6" sx={{ fontSize: 16 }}>
                      {attributeLayerName}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleZoomToSelected}
                      disabled={selectedRows.length === 0}
                      startIcon={<MapIcon sx={{ fontSize: 14 }} />}
                      sx={{
                        color: "white",
                        borderColor: "rgba(255,255,255,0.5)",
                        fontSize: 11,
                        py: 0.2,
                        px: 1,
                        "&:hover": {
                          bgcolor: "rgba(255,255,255,0.15)",
                          borderColor: "white",
                        },
                        "&.Mui-disabled": {
                          color: "rgba(255,255,255,0.4)",
                          borderColor: "rgba(255,255,255,0.2)",
                        },
                      }}
                    >
                      Zoom to Selected
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        setSelectedRows([]);
                        if (selectionLayerRef.current) {
                          selectionLayerRef.current.getSource().clear();
                          selectionLayerRef.current.changed();
                        }
                      }}
                      disabled={selectedRows.length === 0}
                      sx={{
                        color: "white",
                        borderColor: "rgba(255,100,100,0.7)",
                        fontSize: 11,
                        py: 0.2,
                        px: 1,
                        mr: 1,
                        "&:hover": {
                          bgcolor: "rgba(255,100,100,0.2)",
                          borderColor: "white",
                        },
                        "&.Mui-disabled": {
                          color: "rgba(255,255,255,0.4)",
                          borderColor: "rgba(255,255,255,0.2)",
                        },
                      }}
                    >
                      Clear
                    </Button>
                    {/* Undock Button */}
                    <IconButton
                      onClick={() => setAttributeTableDocked(false)}
                      sx={{ color: "white" }}
                      size="small"
                      title="Undock"
                    >
                      <UndockIcon />
                    </IconButton>
                    {/* Close Button */}
                    <IconButton
                      onClick={() => setAttributeTableOpen(false)}
                      sx={{ color: "white" }}
                      size="small"
                    >
                      <CloseIcon />
                    </IconButton>
                  </Box>
                </Box>

                {/* Content */}
                <Box sx={{ flex: 1, overflow: "hidden", bgcolor: "#f5f5f5" }}>
                  {attributeLoading ? (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "100%",
                        flexDirection: "column",
                        gap: 2,
                      }}
                    >
                      <CircularProgress size={60} />
                      <Typography variant="body1" color="text.secondary">
                        Loading attribute data...
                      </Typography>
                    </Box>
                  ) : attributeData.length === 0 ? (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "100%",
                        flexDirection: "column",
                        gap: 2,
                        color: "#999",
                      }}
                    >
                      <LayersIcon sx={{ fontSize: 64, opacity: 0.3 }} />
                      <Typography variant="h6">No data available</Typography>
                    </Box>
                  ) : (
                    <VirtualTable
                      attributeData={attributeData}
                      attributeColumns={attributeColumns}
                      selectedRows={selectedRows}
                      setSelectedRows={setSelectedRows}
                      updateMapSelectionFromRows={updateMapSelectionFromRows}
                      tableViewMode={tableViewMode}
                    />
                  )}
                </Box>

                {/* Footer */}
                <Box
                  sx={{
                    bgcolor: "#f5f5f5",
                    borderTop: "1px solid #e0e0e0",
                    px: 1,
                    py: 0.5,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      border: "1px solid #1976d2",
                      borderRadius: 1,
                      overflow: "hidden",
                    }}
                  >
                    <Button
                      size="small"
                      onClick={() => setTableViewMode("all")}
                      sx={{
                        borderRadius: 0,
                        px: 1.5,
                        py: 0.3,
                        fontSize: 11,
                        minWidth: 0,
                        bgcolor: tableViewMode === "all" ? "#1976d2" : "white",
                        color: tableViewMode === "all" ? "white" : "#1976d2",
                        "&:hover": {
                          bgcolor:
                            tableViewMode === "all" ? "#115293" : "#e3f2fd",
                        },
                      }}
                    >
                      All ({attributeData.length})
                    </Button>
                    <Box sx={{ width: "1px", bgcolor: "#1976d2" }} />
                    <Button
                      size="small"
                      onClick={() => setTableViewMode("selected")}
                      sx={{
                        borderRadius: 0,
                        px: 1.5,
                        py: 0.3,
                        fontSize: 11,
                        minWidth: 0,
                        bgcolor:
                          tableViewMode === "selected" ? "#1976d2" : "white",
                        color:
                          tableViewMode === "selected" ? "white" : "#1976d2",
                        "&:hover": {
                          bgcolor:
                            tableViewMode === "selected"
                              ? "#115293"
                              : "#e3f2fd",
                        },
                      }}
                    >
                      Selected ({selectedRows.length})
                    </Button>
                  </Box>

                  <Button
                    onClick={() => {
                      const exportData =
                        tableViewMode === "selected"
                          ? attributeData.filter((r) =>
                            selectedRows.includes(r.id),
                          )
                          : attributeData;
                      const csv = [
                        attributeColumns.map((col) => col.label).join(","),
                        ...exportData.map((row) =>
                          attributeColumns.map((col) => row[col.id]).join(","),
                        ),
                      ].join("\n");
                      const blob = new Blob([csv], { type: "text/csv" });
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = `${attributeLayerName}_data.csv`;
                      a.click();
                    }}
                    variant="outlined"
                    size="small"
                    startIcon={<CloudDownloadIcon />}
                  >
                    Export CSV
                  </Button>
                </Box>
              </Paper>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
// Print CSS - Add before export
if (typeof document !== "undefined") {
  const printStyles = `
    @media print {
      @page {
        size: A4 landscape;
        margin: 0;
      }
      
      body * {
        visibility: hidden;
      }
      
      #print-container,
      #print-container * {
        visibility: visible;
      }
      
      #print-container {
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        height: 100%;
        padding: 10mm;
      }
    }
  `;

  const styleElement = document.createElement("style");
  styleElement.innerHTML = printStyles;
  if (!document.head.querySelector("style[data-print-styles]")) {
    styleElement.setAttribute("data-print-styles", "true");
    document.head.appendChild(styleElement);
  }
}

export default React.memo(App);
